// Percorso file: src/pages/TeamAssignment.jsx
//
// STRUMENTO ADMIN — assegnazione delle squadre e stato dei membri.
// Grafica: doc 07 §8.1 («estensione strumenti admin»), tavola Design
// docs/design/T3, rese 1d (desktop) e 1e (mobile).
//
// La pagina vive dentro il telaio comune (HomeShell) con la colonna
// larga degli strumenti admin. La lista ha DUE rese dello stesso
// contenuto — card impilate sotto i 768px, tabella sopra — decise dal
// solo CSS condiviso (src/components/AdminTools.css): qui non c'è
// nessuna misura di finestra, nessun ramo per il dispositivo.
//
// Logica, chiamate ed endpoint sono quelli di prima: il cantiere T3-G
// è di sola grafica.

import React, { useEffect, useMemo, useState } from "react";
import { api, SESSION_EXPIRED_MESSAGE } from "../api/api";
import { useAuth } from "../context/AuthContext";
import {
  hasRole,
  canonicalRole,
  normalizza
} from "../utils/roleUtils";
// LA SEDE UNICA DELLA LETTURA DI SQUADRA E DELL'ANNO (cantiere «lettore
// unico»): qui arrivano, già scritte, le tre copie di pagina che stavano
// poco più sotto. `valeSuTutteLeSquadre` non si importa più: la legge il
// modulo, dentro il ripiego di `squadreDellaTessera`.
// `statoDelleTessere` (cantiere «User List per anno guardato»): la regola
// del caso raro — PENDING vince, poi tutte ARCHIVED, altrimenti ACTIVE — che
// questa pagina scriveva due volte (tendina Status e colore di fila) vive
// ora nel modulo, con questo corpo. La legge anche User List (per
// `statoDelSoggetto`, che la compone con `tessereDelSoggetto`), così le due
// pagine dicono lo stesso stato della stessa persona. Questa pagina legge le
// tessere dell'anno una volta sola e passa quell'insieme alla regola: non le
// importa `statoDelSoggetto`, che le rileggerebbe.
import {
  squadreDellaTessera,
  squadreDelSoggetto,
  statoDelleTessere,
  tessereDelSoggetto
} from "../utils/squadre";
import { annoDiLista } from "../utils/stagione";
import { roleLabel } from "../auth/access";
import HomeShell from "../components/HomeShell";
import SeasonStatusBar from "../components/SeasonStatusBar";
import {
  CATEGORIE,
  PastigliaCategoria,
  PastigliaCategoriaSeNota
} from "../components/PastigliaCategoria";
// La categoria ANAGRAFICA (genere + età al 31/12 dell'annata): sede unica in
// `utils/`, la stessa che usa la User List. Da questa fetta la pastiglia di
// questa pagina viene da qui e non più dalla categoria della SQUADRA (doc 07
// §8.1, «Divergenza dichiarata», aggiornata il 28/8/2026).
import { categoriaAnagrafica } from "../utils/categoria";
import "../components/AdminTools.css";
import "./TeamAssignment.css";
import { useNavigate } from "react-router-dom";

/* =========================================================
   COSTANTI E UTILITÀ DI PAGINA
========================================================= */

/** I cinque ruoli del nuovo modello. Nessun "Manager": non esiste più. */
const RUOLI = ["PLAYER", "COACH", "PARENT", "ADMIN", "SUPERADMIN"];

/** I tre stati del contratto. È l'elenco della LETTURA: serve a riconoscere
 *  uno stato (colore della tendina, `classeStato`) e a filtrare la lista.
 *  PENDING resta qui perché una persona in attesa si deve poter VEDERE e
 *  CERCARE — toglierlo di qui spegnerebbe il colore ambra e il filtro. */
const STATI = ["PENDING", "ACTIVE", "ARCHIVED"];

/** Gli stati che un amministratore può SCEGLIERE dalla tendina di stato.
 *  ⚠️ PENDING non c'è: è lo stato in cui una tessera NASCE, non una
 *  destinazione. Su chi è attivo nell'anno operativo il backend rifiuta
 *  quel passo indietro con un 400 («To remove someone from ACTIVE,
 *  archive them», ramo `status === "PENDING"` di `PUT /users/:id/status`),
 *  e una tendina non deve offrire un'azione che fallisce; sugli altri il
 *  400 non scatta, ma retrocedere a PENDING non è comunque un gesto che
 *  questa pagina voglia offrire — la via per il ritorno è la ri-attivazione
 *  (Coda 4), non un passo indietro silenzioso.
 *  Lettura e scelta sono due elenchi diversi di proposito: la tendina
 *  mostra comunque il valore vero della persona (vedi `opzioniStato`). */
const STATI_SELEZIONABILI = ["ACTIVE", "ARCHIVED"];

/** Valore sentinella del filtro squadra: «utenti senza squadra». */
const SENZA_SQUADRA = "__NO_TEAM__";

/** Il rifiuto «da qui non c'è nulla da riammettere» (Coda 4, rientro).
 *  ⚠️ UNA FRASE SOLA, IN UN POSTO SOLO: la dicono due vie diverse — il
 *  RIFIUTO PRECOCE, quando l'anteprima risponde `target.readmittable: false`
 *  prima di chiedere qualunque cosa all'amministratore, e il 409
 *  `NOTHING_TO_READMIT` del server, che è la stessa decisione presa sotto
 *  lucchetto. Due frasi diverse per lo stesso fatto farebbero credere a due
 *  fatti diversi.
 *  ⚠️ È NEUTRA di proposito: non dice «is already active», che sarebbe falso
 *  per l'archiviato vero le cui tessere portano solo grafie storiche e per il
 *  socio vecchio stile senza tessere leggibili. Dice che DA QUI non c'è nulla
 *  da riammettere, e rimanda alla procedura manuale. */
const NULLA_DA_RIAMMETTERE =
  "There is nothing to readmit for this member from here. " +
  "Use Manage Contexts to restore their memberships manually.";

/* =========================================================
   LA STAGIONE — «UNA STAGIONE ALLA VOLTA» (Fetta A)

   Questa pagina vive dentro UN anno alla volta, scelto dal selettore
   «Season Viewed». Dentro quell'anno tutto — chi compare, le righe, i
   colori, lo stato — parla solo di quell'anno, letto DALLA TESSERA di
   quell'anno. La persona di primo livello (`u.status`, `u.role`,
   `u.team`) smette di governare la resa.

   ⚠️ SI LEGGE, NON SI SCRIVE: il dato d'annata arriva già dalla
   proiezione di lista di `GET /users` (`cardSeason` e `status` per ogni
   tessera, più `renewedSeason` sulla persona). Nessuna chiamata nuova,
   nessuna scrittura, nessun tocco al backend.
========================================================= */

/*
 * L'ANNO E LA TESSERA — le copie di pagina NON VIVONO PIÙ QUI.
 *
 * `annoDiLista`, `tesseraDellAnnoGuardato` e `tessereDellAnno` stavano qui
 * come «COPIA LOCALE DI PAGINA, deliberata», in attesa della sede condivisa
 * che quel commento diceva di non voler aprire. Il cantiere «lettore unico»
 * l'ha aperta: `utils/stagione.js` per l'anno, `utils/squadre.js` per la
 * tessera. I corpi sono quelli di qui, invariati.
 *
 * ⚠️ LA DIFFERENZA CON LA USER LIST È PRESERVATA, E ORA È SCRITTA NELLA
 * CHIAMATA. Il predicato gemello di là pretende `status === "ACTIVE"`,
 * perché quella pagina è l'elenco dei soci del club; questa è la pagina in
 * cui si APPROVA, e se le tessere PENDING non comparissero l'amministratore
 * non avrebbe modo di lavorarle proprio da dove le deve lavorare. Entrano
 * quindi PENDING, ACTIVE e ARCHIVED, e lo stato lo dice il colore della
 * riga. Si esprime con `{ stati: null }` — «qualunque stato» — su ogni
 * chiamata di questa pagina: non è un default nascosto, è una scelta
 * dichiarata a ogni punto d'uso.
 *
 * ⚠️ Restano invariate anche le altre due cautele, che ora vivono nel
 * modulo: la PRESENZA della chiave `cardSeason` (non un confronto a `null`,
 * perché la proiezione la emette con uno spread condizionale, e una tessera
 * senz'anno resta fuori — decisione D1 del committente), e l'ANNATA IGNOTA
 * (`anno === null` → la regola dell'anno non si applica e passano tutte).
 */

/*
 * Le cinque categorie ufficiali (`CATEGORIE`) e le due pastiglie
 * (`PastigliaCategoria`, `PastigliaCategoriaSeNota`) NON vivono più qui:
 * sono salite a `components/PastigliaCategoria.jsx`, accanto al CSS che
 * era già condiviso, perché oltre a questa pagina le usa la home del
 * genitore. Il corpo non è cambiato — sono le stesse funzioni, spostate.
 * Si importano in testa al file.
 */

/*
 * La sentinella «ALL» di `approvedTeams` e il suo riconoscimento
 * (`TUTTE_LE_SQUADRE`, `valeSuTutteLeSquadre`) NON vivono più qui: sono
 * saliti a `utils/roleUtils.js` in P7 — e dal cantiere «lettore unico»
 * stanno in `utils/squadre.js`, accanto all'unica regola che li legge, con
 * `roleUtils` che li ri-esporta — perché oltre a questa pagina li legge
 * anche UsersList e due copie della stessa regola divergono sempre.
 * Il corpo non è cambiato — è la stessa funzione, spostata.
 *
 * ⚠️ Da F3b questa pagina la sentinella NON la scrive più: il Save di
 * «Approved Teams» manda `{ teams, allTeams }` alla rotta per-tessera, e i
 * vecchi campi — sentinella compresa — li ricalcola il backend (regola A).
 * Qui non la si legge nemmeno più in proprio: la legge il modulo, dentro il
 * ripiego di `squadreDellaTessera`, che questa pagina ora IMPORTA invece di
 * ricopiarla. Il commento precedente diceva il contrario, ed era vero fino a
 * F3a: si corregge invece di tramandarlo.
 */

/**
 * Etichetta a schermo di un ruolo. Le maiuscole stanno solo nelle
 * pastiglie (doc 07 §3.2), quindi i ruoli si scrivono per esteso.
 *
 * ⚠️ NON C'È PIÙ IL «Team Manager» (F3c). TEAMTD non è mai stato un ruolo:
 * è la vecchia spia scritta nel campo `team` di un coach con squadre
 * approvate, e mostrarla come se fosse un ruolo diceva a chi amministra
 * che quella persona era qualcosa di diverso da un allenatore. Un
 * allenatore con squadre e uno senza sono lo stesso ruolo, e la riga ora
 * scrive «Coach» per entrambi; le squadre le dice la riga stessa, con il
 * loro numero o con «All teams».
 *
 * L'etichetta si prende dal ruolo CANONICO (canonicalRole): «Manager»,
 * «TD» e «ManagerTD» sono allenatori (CLAUDE.md §2) e a schermo si leggono
 * «Coach», non con la loro grafia storica tutta maiuscola. La mappa dei
 * cinque ruoli veri resta una sola, in auth/access.js — `roleLabel` non
 * applica il vocabolario di proposito, quindi lo si applica QUI, che è una
 * resa a schermo e non entra in alcun salvataggio.
 */
function etichettaRuolo(ruolo) {
  return roleLabel(canonicalRole(ruolo));
}

/**
 * Il pezzo « as Player, Parent» della frase di conferma del rientro, o la
 * stringa vuota se i ruoli non sono arrivati.
 *
 * ⚠️ I ruoli li calcola il BACKEND (`target.roles` dell'anteprima): sono
 * quelli che rinascerebbero davvero, non quelli che la riga mostra. Se
 * l'elenco mancasse, la conferma perde il dettaglio ma NON diventa una frase
 * sgrammaticata né una promessa inventata: si tace, e restano l'anno e il
 * numero dei figli.
 */
function frasePerRuoli(ruoli) {
  if (!Array.isArray(ruoli) || ruoli.length === 0) return "";

  return ` as ${ruoli.map(etichettaRuolo).join(", ")}`;
}

/**
 * Le squadre arrivano dal backend come documenti {id, name, category, ...}.
 * Lo stato locale `teams` deve restare SEMPRE un array di {id, name,
 * category, link}: qui c'è l'unico punto che lo costruisce, così
 * add/rename/delete non possono più degradarlo a un array di stringhe.
 * `category` può mancare sui team creati prima di T2-bis: resta "".
 *
 * ⚠️ QUESTA MAPPATURA È UN FILTRO, NON UNA COPIA: `GET /teams` restituisce
 * il documento INTERO, ma qui si ricostruisce un oggetto con i soli campi
 * elencati. Un campo che non compaia in questo elenco viene scartato in
 * silenzio e non arriva mai a schermo — è il motivo per cui `link` sta qui.
 *
 * ⚠️ `link` NON passa da `normalizza` (trim + MAIUSCOLO, giusto per la
 * categoria): un indirizzo in maiuscolo può non risolvere. Si copia com'è.
 * Assente sulle squadre senza indirizzo: resta "".
 */
function mappaTeams(dati) {
  return Array.isArray(dati)
    ? dati
        .filter(Boolean)
        .map((t) =>
          typeof t === "string"
            ? { id: t, name: t, category: "", link: "" }
            : {
                id: t.id,
                name: t.name,
                category: normalizza(t.category),
                link: typeof t.link === "string" ? t.link : ""
              }
        )
        .filter((t) => t.id && t.name)
    : [];
}

/**
 * Classe di colore della tendina Status (doc 07 §8.1, rev. collaudo
 * 5/8/2026): il valore mostrato porta il colore del proprio stato.
 * Uno stato fuori dai tre previsti non ne riceve nessuno: resta nel
 * testo principale, come qualunque altro campo.
 */
function classeStato(stato) {
  const valore = normalizza(stato);
  if (!STATI.includes(valore)) return "";
  return ` qpsc-select--status-${valore.toLowerCase()}`;
}

/**
 * Codice HTTP di un errore di api.js.
 * api.js solleva `Error("API <path> failed: <codice> <corpo>")`: qui si
 * recupera il codice per poter dire all'utente cosa è successo davvero.
 */
function codiceHttp(err) {
  const match = /failed:\s*(\d{3})/.exec(String((err && err.message) || ""));
  return match ? Number(match[1]) : 0;
}

/**
 * Messaggio comprensibile a partire dall'errore di rete.
 * Testi in inglese come il resto della pagina (il club è australiano).
 */
function messaggioErrore(err, fallback) {
  const codice = codiceHttp(err);
  // D8B (RD-166): il 401 è la sessione scaduta, non un permesso negato.
  if (codice === 401) return SESSION_EXPIRED_MESSAGE;
  if (codice === 403) return "You don't have permission to do this.";
  if (codice === 409) return "This team already exists.";
  if (codice === 404) return "Item not found (it may have been deleted).";
  return fallback;
}

/**
 * Il motivo del rifiuto COSÌ COME LO SCRIVE IL SERVER (R9.3), in inglese:
 * `error`, più `detail` quando c'è — la stessa lettura del 400 di Approve.
 * Stringa vuota se il corpo non è leggibile: chi chiama ha il suo ripiego.
 *
 * ⚠️ Serve ai gesti dove `messaggioErrore` direbbe il falso: lì OGNI 409 è
 * «squadra già esistente», mentre sulle rotte delle persone il 409 è la
 * stagione illeggibile (ERRORE_STAGIONE_ILLEGGIBILE). `messaggioErrore`
 * resta invariata per Add/Save/Link/Delete, dove quel 409 è il doppione.
 */
function motivoDalServer(err) {
  try {
    const corpo = JSON.parse(err.body);
    return [corpo?.error, corpo?.detail].filter(Boolean).join(": ");
  } catch (e) {
    return "";
  }
}

/* =========================================================
   TEAM MANAGER MODAL (le squadre di UN'ANNATA, teamConfig/{anno})

   ⚠️ QUESTA MODALE LAVORA DENTRO UN ANNO, e l'anno e' quello del selettore
   «Season Viewed» della pagina (`stagione`). Non e' una raffinatezza: le
   squadre sono per annata (teams-season, D1/D3), quindi l'elenco che si
   vede, il nome che si crea, quello che si rinomina e quello che si cancella
   appartengono TUTTI a quell'anno e a nessun altro. Le quattro scritture lo
   mandano al backend, che senza di esso rifiuta.
========================================================= */
function TeamManagerModal({
  open,
  onClose,
  teams,
  onTeamsChange,
  onUsersChange,
  isSuperAdmin,
  stagione
}) {
  const [newTeam, setNewTeam] = useState("");
  // Nessuna categoria preselezionata: la scelta dev'essere esplicita.
  const [newCategory, setNewCategory] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editCategory, setEditCategory] = useState("");
  // Squadra in attesa di conferma per l'eliminazione: SOLO stato di
  // interfaccia, non tocca né dati né chiamate.
  const [deletingId, setDeletingId] = useState(null);
  // Squadra di cui si sta modificando l'indirizzo: QUARTO stato della riga,
  // stesso calco di `editingId`. Anche questo è solo interfaccia.
  const [linkingId, setLinkingId] = useState(null);
  const [linkValue, setLinkValue] = useState("");
  const [alert, setAlert] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    if (open) {
      setAlert("");
      setInfo("");
      setNewTeam("");
      setNewCategory("");
      setEditingId(null);
      setEditValue("");
      setEditCategory("");
      setDeletingId(null);
      setLinkingId(null);
      setLinkValue("");
    }
  }, [open]);

  if (!open) return null;

  /**
   * Ricarica l'elenco DELL'ANNATA dal backend, mantenendo la forma
   * {id, name, category, link}.
   *
   * ⚠️ Anno non calcolabile: la chiamata parte NUDA e il backend risponde con
   * l'anno operativo. E' la stessa regola con cui la pagina tratta le tessere
   * quando l'annata non si sa leggere — non si fa sparire nessuno.
   */
  const ricaricaTeams = async () => {
    const percorso = Number.isInteger(stagione)
      ? `/teams?season=${encodeURIComponent(stagione)}`
      : "/teams";

    const aggiornati = await api.get(percorso);
    onTeamsChange(mappaTeams(aggiornati));
  };

  // Ricarica fallita dopo una scrittura riuscita (G5-d): calco di
  // MSG_RICARICA in UniformConfig.jsx. Esce in `alert`, che copre `info`.
  const MSG_RICARICA =
    "Saved, but the team list could not be reloaded. Reload the page before saving again.";

  /**
   * Le quattro scritture di questa modale vivono TUTTE dentro un'annata:
   * senza un anno intero non c'e' nessuna annata su cui scrivere, e il
   * backend rifiuta con 400. Ci si ferma prima, e si dice perche'.
   */
  const stagioneMancante = () => {
    if (Number.isInteger(stagione)) return false;

    setInfo("");
    setAlert("Season not available: teams cannot be changed right now.");
    return true;
  };

  /** Vero se esiste già una squadra con quel nome (confronto insensibile). */
  const nomeGiaUsato = (nome, escludiId = null) =>
    teams.some(
      (t) => t.id !== escludiId && normalizza(t.name) === normalizza(nome)
    );

  /** Riepilogo leggibile del riallineamento eseguito dal backend. */
  const riepilogoRename = (esito) => {
    if (!esito || !esito.renamed) return "Team updated.";

    const parti = [`${esito.usersUpdated || 0} users updated`];
    if (esito.contextsUpdated) parti.push(`${esito.contextsUpdated} contexts realigned`);
    if (esito.approvedTeamsUpdated) {
      parti.push(`${esito.approvedTeamsUpdated} approved-team lists realigned`);
    }
    if (esito.eventsUpdated) parti.push(`${esito.eventsUpdated} events`);
    if (esito.attendanceUpdated) parti.push(`${esito.attendanceUpdated} attendance records`);
    if (esito.gamecallupsUpdated) parti.push(`${esito.gamecallupsUpdated} game call-ups`);
    if (esito.chatsUpdated) parti.push(`${esito.chatsUpdated} team chats`);
    if (esito.uniformConfigUpdated) parti.push("uniform config realigned");

    // Un campo solo per gli avvisi, e ora ne puo' portare due: le statistiche
    // delle Cups da ricostruire, e i record lasciati col nome vecchio perche'
    // il loro anno non si e' potuto leggere. Qui non cambia nulla — resta una
    // stringa, e si mostra com'e'.
    const avviso = esito.warning ? ` ${esito.warning}.` : "";

    // ⚠️ L'ANNO SI DICE. Con lo stesso nome vivo in piu' annate, un riepilogo
    // che non nomini la stagione toccata non si puo' leggere.
    const annata = Number.isInteger(esito.season) ? ` for ${esito.season}` : "";

    return `Renamed${annata}. ${parti.join(", ")}.${avviso}`;
  };

  // ADD TEAM — nell'annata guardata.
  //
  // ⚠️ `season` NEL CORPO, ed e' il punto che chiude il baco piu' facile da
  // fare (teams-season, G5/C11): senza, il backend scriverebbe nell'anno
  // OPERATIVO, e l'amministratore che guarda il 2026 creerebbe squadre nel
  // 2027 — che poi non vedrebbe comparire nell'elenco.
  const handleAdd = async () => {
    const name = newTeam.trim();
    if (!name) return;

    if (stagioneMancante()) return;

    // La categoria è obbligatoria: il backend rifiuta comunque, ma qui
    // l'admin lo scopre subito e senza un errore di rete.
    if (!newCategory) {
      setAlert("Choose a category for the new team.");
      return;
    }

    if (nomeGiaUsato(name)) {
      setAlert("This team already exists.");
      return;
    }

    try {
      await api.post("/teams", {
        name,
        category: newCategory,
        season: stagione
      });
    } catch (err) {
      setInfo("");
      setAlert(messaggioErrore(err, "Error adding team."));
      return;
    }

    // ⚠️ LA RICARICA STA FUORI DAL TRY DELLA SCRITTURA: la scrittura e'
    // riuscita, e un guasto nel rileggere l'elenco non deve presentarsi come
    // «Error adding team.». L'elenco resta quello di prima e lo si dice con
    // MSG_RICARICA (G5-d). Stessa forma nelle altre tre scritture della modale.
    let ricaricata = true;
    try {
      await ricaricaTeams();
    } catch (err) {
      // Ricarica fallita: la scrittura resta riuscita, si prosegue — e si dice (G5-d).
      ricaricata = false;
    }

    setNewTeam("");
    setNewCategory("");
    setAlert(ricaricata ? "" : MSG_RICARICA);
    setInfo(`Team "${name}" created for ${stagione}.`);
  };

  // SAVE TEAM (nome e/o categoria) — dentro l'annata guardata.
  //
  // ⚠️ `season` NEL CORPO: e' cio' che circoscrive il riallineamento. Il
  // backend rinomina solo le tessere di QUELL'anno, solo i satelliti di
  // quell'anno e solo i gruppi di chat di quell'anno: rinominare la «U12» del
  // 2027 non deve toccare una riga del 2026.
  const handleSave = async (teamObj, newName, newCat) => {
    const name = newName.trim();
    if (!name) {
      setAlert("Invalid team name.");
      return;
    }

    if (stagioneMancante()) return;

    if (!newCat) {
      setAlert("Choose a category for this team.");
      return;
    }

    if (nomeGiaUsato(name, teamObj.id)) {
      setAlert("This team already exists.");
      return;
    }

    let esito;
    try {
      esito = await api.put(`/teams/${encodeURIComponent(teamObj.id)}`, {
        name,
        category: newCat,
        season: stagione
      });
    } catch (err) {
      setInfo("");
      setAlert(messaggioErrore(err, "Error updating team."));
      return;
    }

    // Il rename riallinea utenti, contexts e configurazione divise:
    // per questo si ricaricano ANCHE gli utenti, non solo i team.
    // Ricarica FUORI dal try della scrittura (vedi handleAdd).
    let ricaricata = true;
    try {
      await ricaricaTeams();
      if (onUsersChange) await onUsersChange();
    } catch (err) {
      // Ricarica fallita: la scrittura resta riuscita, si prosegue — e si dice (G5-d).
      ricaricata = false;
    }

    setEditingId(null);
    setEditValue("");
    setEditCategory("");
    setAlert(ricaricata ? "" : MSG_RICARICA);
    setInfo(riepilogoRename(esito));
  };

  // ------------------------------------------------------------------
  // LINK DELLA SQUADRA (solo SUPERADMIN)
  // ------------------------------------------------------------------

  /**
   * Validazione UNICA dell'indirizzo, condivisa da Test e da Save: deve
   * sembrare un URL, cioè cominciare con http:// o https://. Non è una
   * verifica che l'indirizzo esista — a quello serve Test.
   */
  const linkValido = (valore) =>
    /^https?:\/\//i.test(String(valore || "").trim());

  /**
   * Test: apre in una scheda nuova l'indirizzo DIGITATO IN QUEL MOMENTO,
   * non quello salvato — serve proprio a provarlo prima di salvarlo.
   * `noopener` perché la pagina aperta non deve poter toccare questa.
   */
  const apriProva = (valore) => {
    const url = String(valore || "").trim();
    if (!linkValido(url)) {
      setInfo("");
      setAlert("Invalid link.");
      return;
    }
    window.open(url, "_blank", "noopener");
  };

  /**
   * Save del link. Rotta propria, riservata al SUPERADMIN dal backend
   * (PUT /teams/:id/link): qui il gate dell'interfaccia è solo una
   * cortesia, il vero cancello è di là.
   *
   * ⚠️ NON chiama `onUsersChange`: il link non è portato in copia da
   * nessun'altra collezione, quindi non c'è nulla da riallineare e nessun
   * riepilogo da mostrare.
   *
   * Casella vuota = rimozione del campo, ed è un esito legittimo: solo un
   * valore NON VUOTO e malformato viene rifiutato.
   */
  const handleSaveLink = async (teamObj, valore) => {
    const link = String(valore || "").trim();

    if (link && !linkValido(link)) {
      setInfo("");
      setAlert("Invalid link.");
      return;
    }

    if (stagioneMancante()) return;

    try {
      // `season` nel corpo: lo stesso nome puo' vivere in piu' annate, su
      // squadre diverse, e l'indirizzo e' di UNA di esse.
      await api.put(`/teams/${encodeURIComponent(teamObj.id)}/link`, {
        link,
        season: stagione
      });
    } catch (err) {
      setInfo("");
      setAlert(messaggioErrore(err, "Error updating link."));
      return;
    }

    // Ricarica FUORI dal try della scrittura (vedi handleAdd).
    let ricaricata = true;
    try {
      await ricaricaTeams();
    } catch (err) {
      // Ricarica fallita: la scrittura resta riuscita, si prosegue — e si dice (G5-d).
      ricaricata = false;
    }

    setLinkingId(null);
    setLinkValue("");
    setAlert(ricaricata ? "" : MSG_RICARICA);
    setInfo(link ? "Link saved." : "Link removed.");
  };

  // DELETE TEAM — la squadra DI QUELL'ANNATA, e con l'avviso sulle code.
  //
  // ⚠️ `?season=` NELLA QUERY, non nel corpo: `api.del` un corpo non lo manda.
  // Il backend senza annata rifiuta con 400, e fa bene: si cancella la
  // squadra di UN anno, non un nome.
  //
  // ⚠️ LA DOPPIA CONFERMA NON E' SEMPRE. Il backend risponde
  // `code: "CONFIRM_REQUIRED"` SOLO se la squadra ha uno storico (una chat di
  // squadra con messaggi, o eventi di quell'anno); senza storico cancella e
  // non chiede nulla. E' la stessa asimmetria di `archiviaFamiglia`, che
  // chiede solo quando c'e' davvero qualcosa da perdere.
  //
  // ⚠️ La FORMA della conferma e' quella di casa — una domanda secca, poi la
  // parola da digitare per esteso, confronto SECCO e maiuscolo: non si
  // inventa una modale nuova per un gesto distruttivo.
  const handleDelete = async (teamObj, confermato = false) => {
    if (stagioneMancante()) return;

    try {
      await api.del(
        `/teams/${encodeURIComponent(teamObj.id)}` +
          `?season=${encodeURIComponent(stagione)}` +
          (confermato ? "&confirm=1" : "")
      );
    } catch (err) {
      setInfo("");

      // ⚠️ IL MOTIVO DEL RIFIUTO ARRIVA A CHI HA PREMUTO. Il 409 della
      // cancellazione dice QUANTI membri sono ancora assegnati: passarlo a
      // `messaggioErrore` risponderebbe "This team already exists.", che qui
      // è falso. Il testo lo scrive il backend, che è l'unico a saperne il
      // numero; `messaggioErrore` resta la voce di tutti gli altri esiti.
      if (err?.status === 409) {
        let motivo = "";
        let codice = "";

        try {
          const corpo = JSON.parse(err.body);
          motivo = corpo?.error || "";
          codice = corpo?.code || "";
        } catch (e) {
          // Corpo non leggibile: resta il messaggio generico qui sotto.
        }

        if (codice === "CONFIRM_REQUIRED" && !confermato) {
          const proseguo = window.confirm(
            `${motivo} Delete it anyway?`
          );

          if (!proseguo) {
            setAlert("");
            return;
          }

          const doppia = window.prompt('Type "DELETE" to confirm:');

          if (doppia !== "DELETE") {
            window.alert("Cancelled");
            return;
          }

          // Secondo giro, con la conferma in mano. Il backend ripete tutte le
          // sue guardie: la conferma non salta niente, dice solo «ho letto».
          await handleDelete(teamObj, true);
          return;
        }

        setAlert(motivo || "Error deleting team.");
        return;
      }

      setAlert(messaggioErrore(err, "Error deleting team."));
      return;
    }

    // Ricarica FUORI dal try della scrittura (vedi handleAdd).
    let ricaricata = true;
    try {
      await ricaricaTeams();
    } catch (err) {
      // Ricarica fallita: la scrittura resta riuscita, si prosegue — e si dice (G5-d).
      ricaricata = false;
    }

    setDeletingId(null);
    setAlert(ricaricata ? "" : MSG_RICARICA);
    setInfo(`Team "${teamObj.name}" deleted from ${stagione}.`);
  };

  return (
    <div className="qpsc-modal-overlay">
      {/* `ta-manage-modal` è la classe MODIFICATORE di questa sola finestra:
          i fogli CRA sono globali, e senza uno scope proprio ogni regola
          desktop colpirebbe TUTTE le modali del kit. Stesso meccanismo del
          modale Payments (`qpsc-modal qpsc-pay-modal`, cantiere UL-VESTE).
          Le regole che la usano stanno in TeamAssignment.css, dentro la
          media query ≥768px: sotto quella soglia la classe non fa nulla. */}
      <div className="qpsc-modal ta-manage-modal">
        {/* ⚠️ L'ANNO SI DICE, anche qui: l'elenco sotto e' quello di UNA
            annata, e una modale che non la nominasse farebbe credere di
            gestire «le squadre del club». Stessa regola della conferma
            scritta dell'archiviazione. */}
        <h2 className="qpsc-modal__title">
          {Number.isInteger(stagione) ? `Manage Teams — ${stagione}` : "Manage Teams"}
        </h2>

        <div className="qpsc-modal__body">
          <div className="ta-team-form">
            <div className="qpsc-field">
              <span className="qpsc-field__label">New team</span>
              <input
                className="qpsc-input"
                type="text"
                value={newTeam}
                onChange={(e) => setNewTeam(e.target.value)}
                placeholder="New Team Name"
              />
            </div>

            {/* §6 GRUPPO-SCELTA: la categoria è una scelta multipla, non
                un campo di form. Contenitore con bordo oro ed etichetta,
                cinque opzioni come pulsanti scuri §5.2. Nessun default:
                la categoria si sceglie, non si eredita. Il valore scritto
                resta lo stesso di prima (una delle cinque, MAIUSCOLO). */}
            <div className="qpsc-choice-group">
              <span className="qpsc-choice-group__label">Category</span>
              <div className="qpsc-choice-group__options">
                {CATEGORIE.map((categoria) => (
                  <button
                    key={categoria}
                    type="button"
                    className={
                      "qpsc-btn-dark" +
                      (newCategory === categoria
                        ? " qpsc-choice-option--selected"
                        : "")
                    }
                    aria-pressed={newCategory === categoria}
                    onClick={() => setNewCategory(categoria)}
                  >
                    <PastigliaCategoria category={categoria} />
                  </button>
                ))}
              </div>
            </div>

            <button onClick={handleAdd} className="qpsc-tool-btn qpsc-tool-btn--wide">
              Add
            </button>
          </div>

          <div className="ta-team-list">
            {teams.map((team) => (
              <div key={team.id} className="ta-team-row">
                {editingId === team.id ? (
                  <>
                    <div className="qpsc-field">
                      <span className="qpsc-field__label">Name</span>
                      <input
                        className="qpsc-input"
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                      />
                    </div>

                    <div className="qpsc-field">
                      <span className="qpsc-field__label">Category</span>
                      <select
                        className="qpsc-select"
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                      >
                        <option value="">Category…</option>
                        {CATEGORIE.map((categoria) => (
                          <option key={categoria} value={categoria}>{categoria}</option>
                        ))}
                      </select>
                    </div>

                    <div className="ta-team-row__actions">
                      <button
                        onClick={() => handleSave(team, editValue, editCategory)}
                        className="qpsc-tool-btn qpsc-tool-btn--wide"
                      >
                        Save
                      </button>
                      <button
                        className="qpsc-ghost-btn qpsc-ghost-btn--wide"
                        onClick={() => {
                          setEditingId(null);
                          setEditValue("");
                          setEditCategory("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : deletingId === team.id ? (
                  /* Conferma DENTRO la modale: nessuna finestra del browser.
                     È solo interfaccia — la chiamata che parte è la stessa di
                     prima, invariata. Resta impilata: nome, domanda, comandi. */
                  <>
                    <span className="ta-team-row__name">
                      {team.name}
                      <PastigliaCategoria category={team.category} />
                    </span>

                    <p className="ta-team-row__confirm">
                      Delete "{team.name}"? This cannot be undone.
                    </p>

                    <div className="ta-team-row__actions">
                      <button
                        className="qpsc-tool-btn qpsc-tool-btn--wide"
                        onClick={() => handleDelete(team)}
                      >
                        Delete
                      </button>

                      <button
                        className="qpsc-ghost-btn qpsc-ghost-btn--wide"
                        onClick={() => setDeletingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : linkingId === team.id ? (
                  /* Quarto stato: l'indirizzo della squadra. Stessa forma
                     dell'edit — campo in linea e comandi sotto — più Test,
                     che apre l'indirizzo DIGITATO in una scheda nuova.
                     Nessuna regola grafica nuova: i tre comandi si dividono
                     la riga da soli (.ta-team-row__actions > * { flex: 1 }). */
                  <>
                    <span className="ta-team-row__name">
                      {team.name}
                      <PastigliaCategoria category={team.category} />
                    </span>

                    <div className="qpsc-field">
                      <span className="qpsc-field__label">Link</span>
                      <input
                        className="qpsc-input"
                        type="text"
                        value={linkValue}
                        onChange={(e) => setLinkValue(e.target.value)}
                        placeholder="https://…"
                      />
                    </div>

                    <div className="ta-team-row__actions">
                      <button
                        className="qpsc-ghost-btn qpsc-ghost-btn--wide"
                        disabled={!linkValido(linkValue)}
                        onClick={() => apriProva(linkValue)}
                      >
                        Test
                      </button>

                      <button
                        className="qpsc-tool-btn qpsc-tool-btn--wide"
                        onClick={() => handleSaveLink(team, linkValue)}
                      >
                        Save
                      </button>

                      <button
                        className="qpsc-ghost-btn qpsc-ghost-btn--wide"
                        onClick={() => {
                          setLinkingId(null);
                          setLinkValue("");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  /* Stato normale: UNA linea sola — nome con pastiglia a
                     sinistra, Edit, LINK (solo SUPERADMIN) e Delete
                     (fantasma compatto §5.3) a destra, dentro i 420px della
                     modale. */
                  <div className="ta-team-row__line">
                    <span className="ta-team-row__name">
                      {team.name}
                      <PastigliaCategoria category={team.category} />
                    </span>

                    <div className="ta-team-row__actions ta-team-row__actions--inline">
                      <button
                        className="qpsc-ghost-btn"
                        onClick={() => {
                          setDeletingId(null);
                          setLinkingId(null);
                          setEditingId(team.id);
                          setEditValue(team.name);
                          setEditCategory(
                            CATEGORIE.includes(normalizza(team.category))
                              ? normalizza(team.category)
                              : ""
                          );
                        }}
                      >
                        Edit
                      </button>

                      {/* Terzo comando, SOLO per il SUPERADMIN: per un ADMIN
                          la riga resta a due bottoni come prima. Il gate qui
                          è d'interfaccia; quello vero sta sulla rotta. */}
                      {isSuperAdmin && (
                        <button
                          className="qpsc-ghost-btn"
                          onClick={() => {
                            setEditingId(null);
                            setDeletingId(null);
                            setLinkingId(team.id);
                            setLinkValue(team.link || "");
                          }}
                        >
                          LINK
                        </button>
                      )}

                      <button
                        className="qpsc-ghost-btn"
                        onClick={() => {
                          setEditingId(null);
                          setLinkingId(null);
                          setDeletingId(team.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {alert && <p className="ta-alert">{alert}</p>}
        {!alert && info && <p className="ta-info">{info}</p>}

        <div className="qpsc-modal__actions">
          <button className="qpsc-ghost-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   TEAM ASSIGNMENT
========================================================= */
export default function TeamAssignment() {
  // ⚠️ I DUE ANNI DEL CLUB VENGONO DALLA SEDE CONDIVISA (AuthContext, YS-5),
  // già normalizzati a intero, insieme allo stato della loro lettura. Nessuna
  // chiamata propria a `GET /season-config`: un solo posto dice l'annata.
  const {
    user,
    operatingSeason,
    enrollmentSeason,
    seasonStatus
  } = useAuth();

  // Gate unico (src/auth/access.js): admin per `role` di primo livello
  // OPPURE per un context ACTIVE con ruolo admin. Mai confronti a mano.
  const isAdmin = hasRole(user, "ADMIN", "SUPERADMIN");

  // Stesso gate, un ruolo solo: serve al comando LINK della modale Manage
  // Teams, riservato al SUPERADMIN (precedente vivo: MemberProfileModal).
  const isSuperAdmin = hasRole(user, "SUPERADMIN");

  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // L'annata che l'amministratore ha SCELTO di guardare. `undefined` = non ha
  // ancora scelto, e allora vale il default calcolato (`stagionePredefinita`).
  // ⚠️ Nessun `useEffect` che la scriva alla prima occasione: un default
  // assegnato per effetto va poi difeso da sé stesso, e ogni difesa è un punto
  // in cui la scelta dell'amministratore può essere sovrascritta alle sue
  // spalle. Calcolarlo lo rende impossibile per costruzione (stesso pattern
  // della User List).
  const [stagioneScelta, setStagioneScelta] = useState(undefined);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  /* LA MODALITÀ FAMIGLIA (cantiere FAMIGLIA-LISTE, 9/9/2026).
     ⚠️ NON È UN FILTRO, ed è il motivo per cui non sta insieme ai quattro
     qui sopra né si chiama «Filter by…»: non restringe l'elenco di una
     riga — aggiunge, sotto il nome, chi sta in famiglia con quella persona.
     Spenta di default, e per-sessione: la pagina non persiste nessuna
     preferenza di vista, nemmeno «Season Viewed», e questa non inaugura
     una consuetudine nuova da sola. */
  const [modalitaFamiglia, setModalitaFamiglia] = useState(false);

  const [teamTDUser, setTeamTDUser] = useState(null);
  // La TESSERA su cui la finestra sta lavorando (F3b). Prima la finestra si
  // apriva sulla PERSONA e cercava da sé «la» tessera coach: con due tessere
  // coach — o con una persona che è coach e giocatore — quel «la» non esiste,
  // ed è la stessa ambiguità che questa fetta chiude. Qui la tessera arriva
  // dalla RIGA che ha premuto il bottone, e il suo `id` è ciò che parte al
  // Save.
  const [teamTDTessera, setTeamTDTessera] = useState(null);
  const [selectedTeams, setSelectedTeams] = useState([]);
  // Spunta «All teams» della modale (doc 07 §8.1, rev. 6/8/2026): tiene
  // la sentinella SEPARATA dall'elenco esplicito, così i due non possono
  // mai convivere — né a schermo né in ciò che parte al Save.
  const [tutteLeSquadre, setTutteLeSquadre] = useState(false);
  // Filtro di RICERCA della modale Approved Teams (cantiere
  // ASSIGNTEAM-DESKTOP, 10/9/2026). È uno stato di SOLA VISTA: restringe
  // l'elenco che si vede, non tocca né `selectedTeams` né `tutteLeSquadre`.
  // Una squadra spuntata e poi filtrata via resta spuntata e il Save la
  // salva: il campo nasconde righe, non cambia scelte. Il campo si vede
  // solo da 768px in su (TeamAssignment.css), quindi sotto quel confine
  // questo stato resta "" per costruzione e l'elenco è quello di sempre.
  const [teamSearchQuery, setTeamSearchQuery] = useState("");

  // RIENTRO DELL'ARCHIVIATO (Coda 4, fetta R4) — i due stati che reggono la
  // modale di selezione dei figli, sul modello ESATTO della modale Approved
  // Teams qui sopra (`teamTDUser` & c.): l'oggetto vale `null` a finestra
  // chiusa, e la sua sola presenza è ciò che la fa comparire nel `return`.
  //
  // ⚠️ IL FLUSSO È SPEZZATO, non atteso. La selezione dei figli passa da una
  // modale React guidata da stato, che NON è attendibile con un `await` dentro
  // una funzione asincrona — a differenza di `archiviaFamiglia`, che usa i
  // dialoghi bloccanti del browser e può restare tutta in una funzione. Il
  // gesto vive quindi in tre pezzi: `riammettiFamiglia` legge e apre,
  // `confermaRientro` (l'handler del bottone) conferma e scrive,
  // `chiudiRientroModale` azzera. È la forma già in casa
  // (`openTeamTDModal` → `saveApprovedTeams` → `chiudiTeamTDModal`), e non si
  // inventa un resolver di promise tenuto in stato per un gesto che scrive su
  // documenti di minori.
  const [rientroModale, setRientroModale] = useState(null);
  // Gli id dei figli SPUNTATI. Parte vuoto e resta vuoto finché l'ammini-
  // stratore non sceglie: nessuna spunta di default, nessuna cascata cieca.
  const [figliScelti, setFigliScelti] = useState([]);

  const [showTeamManager, setShowTeamManager] = useState(false);

  // ⚠️ `selectedProfile` non esiste più (cantiere View Profile, fase 3): la
  // scheda del tesserato è una PAGINA su rotta propria e «View Profile» ci
  // naviga, invece di aprire una finestra sopra questa. Cade con lo stato
  // anche l'`onUpdate` che rileggeva `/users`: al ritorno questa pagina si
  // rimonta e rilegge da sé.
  const navigate = useNavigate();

  const teamNames = useMemo(
    () => teams.map((t) => t.name).filter(Boolean),
    [teams]
  );

  /**
   * Gli stessi nomi, per la verifica di appartenenza del contatore
   * «N approved teams». Il criterio è quello — identico — con cui la
   * modale Approved Teams decide quali caselle spuntare
   * (`selectedTeams.includes(team)` su `teamNames`): così le due rese
   * dello stesso dato non possono più dire numeri diversi.
   */
  const insiemeTeam = useMemo(() => new Set(teamNames), [teamNames]);

  /*
   * ⛔️ RIMOSSA nella Fetta A — la funzione di pagina che dava la categoria
   * della SQUADRA a partire dal suo nome, mappando `teams`.
   *
   * Serviva alla sola pastiglia accanto al nome, che ora dice la categoria
   * ANAGRAFICA della persona (`categoriaAnagrafica`, sede unica in
   * `utils/categoria.js`): la squadra non è più la sorgente, e la funzione
   * non ha altri lettori. Cade con lei l'ultima lettura di `u.team` in
   * questa pagina — il campo di primo livello che il modello a tessere ha
   * sostituito.
   *
   * ⚠️ `teams` NON cade: resta l'elenco delle squadre del club, che serve
   * alla modale «Manage Teams», alla modale delle squadre e alle voci
   * assegnabili di una tessera (`teamNames`, `insiemeTeam`).
   */

  /* =======================
     LE ANNATE SCEGLIBILI, E QUALE È ATTIVA (Fetta A)
     Stesso pattern della User List, copiato e non reinventato.
  ======================= */

  // Le annate presenti nei DATI. Qui la sorgente sono le TESSERE — è la
  // pagina delle tessere — più `season`/`seasons[]` della persona, che sono
  // gli stessi campi da cui la pagina gemella costruisce la sua tendina: una
  // stagione in cui una persona è socia ma non ha (ancora) tessere è
  // un'annata che esiste, e va potuta guardare.
  const annateDeiDati = useMemo(
    () =>
      Array.from(
        users.reduce((elenco, u) => {
          (Array.isArray(u?.contexts) ? u.contexts : []).forEach((ctx) => {
            const anno = annoDiLista(ctx?.cardSeason);
            if (anno !== null) elenco.add(anno);
          });
          (Array.isArray(u?.seasons) ? u.seasons : []).forEach((voce) => {
            const anno = annoDiLista(voce);
            if (anno !== null) elenco.add(anno);
          });
          const anno = annoDiLista(u?.season);
          if (anno !== null) elenco.add(anno);
          return elenco;
        }, new Set())
      ).sort((a, b) => b - a),
    [users]
  );

  // L'unione con i DUE anni del club. `Set` toglie i doppioni: in stagione
  // unica operativo e iscrizione sono lo stesso numero e devono comparire una
  // volta sola. Al rollover, invece, l'annata nuova è vuota e senza questa
  // unione non comparirebbe: un'annata vuota che si può scegliere dice il
  // vero («nessuno ancora»), un'annata che manca non dice niente.
  const stagioniDisponibili = useMemo(
    () =>
      Array.from(
        [operatingSeason, enrollmentSeason].reduce(
          (elenco, anno) => (anno == null ? elenco : elenco.add(anno)),
          new Set(annateDeiDati)
        )
      ).sort((a, b) => b - a),
    [annateDeiDati, operatingSeason, enrollmentSeason]
  );

  // Il DEFAULT — si calcola, non si assegna. I tre casi, in ordine:
  //   - stagione letta              → l'annata OPERATIVA del club (la pagina
  //                                   si apre su chi gioca ADESSO);
  //   - lettura fallita ("error")   → l'annata più recente presente nei dati;
  //   - lettura in volo ("loading") → `null`, nessun default: si mostra tutto
  //                                   invece di sfarfallare su un'annata
  //                                   sbagliata per poi cambiarla.
  const stagionePredefinita =
    operatingSeason != null
      ? operatingSeason
      : seasonStatus === "error"
        ? (annateDeiDati.length > 0 ? annateDeiDati[0] : null)
        : null;

  // L'annata effettivamente guardata: la scelta se c'è, altrimenti il default.
  // Vale `null` in un caso solo — default non calcolabile — e allora la regola
  // dell'anno non si applica alle tessere (`tesseraDellAnno` del modulo): su un
  // dato che non si sa leggere non si fa sparire nessuno.
  const stagioneAttiva =
    stagioneScelta !== undefined ? stagioneScelta : stagionePredefinita;

  // STO GUARDANDO L'ANNO D'ISCRIZIONE? — il permesso dell'evidenza «da
  // completare» (riga ambra + label INCOMPLETE, più sotto).
  //
  // ⚠️ `mustCompleteSeason` parla dell'anno di ENROLLMENT e di quello solo:
  // è la risposta giusta LÌ (anagrafica + famiglia + tessera), e non dice
  // niente delle altre annate. Mostrarla mentre si guarda un'altra stagione
  // marcava il rinnovante sul 2026 per una mancanza del 2027. Qui il flag si
  // riusa dov'è vero e si spegne altrove: nessuna verità ricostruita.
  //
  // ⚠️ `Number.isInteger` PRIMA dell'uguaglianza, non per pignoleria: con
  // annata ignota (`stagioneAttiva === null`) ed `enrollmentSeason` non letto
  // (`null` dalla sede condivisa) un `===` crudo direbbe «sì» per caso, e
  // l'evidenza comparirebbe su un anno che nessuno ha dichiarato.
  //
  // ⚠️ `annoDiLista` NON è ridondante anche se qui `stagioneAttiva` è già
  // intero (la tendina converte con `Number()` nel suo `onChange`, i default
  // vengono da `annoDiLista`/AuthContext): è il metro che questo file usa già
  // per confrontare un anno con `stagioneAttiva` (v. `haRinnovatoPerLAnno`),
  // è idempotente su un intero, e tiene il confronto onesto se un domani la
  // tendina lasciasse passare la stringa del DOM.
  //
  // ⚠️ E LO STESSO METRO VA SUI DUE LATI, non solo a sinistra: oggi
  // `enrollmentSeason` arriva già intero o `null` dalla sede condivisa
  // (AuthContext), quindi `annoDiLista` lì è idempotente e il booleano non
  // cambia di un bit. Ma se un domani quel lato arrivasse come stringa, un
  // confronto asimmetrico direbbe «no» sempre e spegnerebbe l'evidenza IN
  // SILENZIO, ovunque. Simmetrico, il guasto non può nascondersi.
  const annoGuardato = annoDiLista(stagioneAttiva);
  const annoIscrizione = annoDiLista(enrollmentSeason);
  const guardoAnnoIscrizione =
    Number.isInteger(annoGuardato) && annoGuardato === annoIscrizione;

  /* =======================
     LA COORTE D'ANNATA — chi compare (Fetta A, lavoro F)

     Compare chi ha ALMENO UNA tessera dell'anno guardato, in QUALUNQUE
     stato — OPPURE è ADMIN / SUPERADMIN.

     ⚠️ L'ECCEZIONE ADMIN è un OPPURE, non un filtro in più, ed è un fatto
     del dato: le tessere amministrative non portano `cardSeason` e non
     possono portarlo, quindi cercarle «per annata» non ne troverebbe mai
     una e il primo effetto della regola nuova sarebbe far sparire gli
     amministratori dalla pagina che governano.

     ⚠️ Il gate dei ruoli è quello UNICO (`hasRole` → auth/access.js,
     CLAUDE.md §2): qui non se ne scrive un secondo.
  ======================= */
  const coorteDiStagione = useMemo(
    () =>
      users.filter(
        (u) =>
          tessereDelSoggetto(u?.contexts, stagioneAttiva, { stati: null })
            .length > 0 || hasRole(u, "ADMIN", "SUPERADMIN")
      ),
    [users, stagioneAttiva]
  );

  /* =======================
     LA FAMIGLIA — I DUE VERSI DEL LEGAME, RICOMPOSTI SENZA UNA CHIAMATA
     (cantiere FAMIGLIA-LISTE, 9/9/2026)

     Da questa fetta `GET /users` porta i legami come SOLI ID — `guardians`
     sul figlio, `linkedProfileIds` sul genitore. I nomi non viaggiano e non
     servono: l'elenco che la pagina ha in mano è INTEGRALE, quindi le persone
     ci sono già tutte e il legame si ricompone incrociando gli id con loro.
     Nessuna chiamata in più, nessuna lettura per riga.

     ⚠️ SI INDICIZZA `users`, NON `coorteDiStagione` E NON `blocchi`: la
     coorte è il taglio dell'annata guardata, i `blocchi` sono già passati per
     i quattro filtri. Cercare lì dentro perderebbe in silenzio proprio i
     collegati che una riga propria non ce l'hanno.
  ======================= */
  const indiceDiPersona = useMemo(() => {
    const perId = new Map();

    users.forEach((u) => {
      const id = String((u && u.id) || "");
      if (id) perId.set(id, u);
    });

    return perId;
  }, [users]);

  /* L'INDICE INVERSO, dal tutore ai suoi figli.
     ⚠️ LA SORGENTE È `guardians` SUL FIGLIO — la faccia autorevole del
     legame, la stessa che il backend interroga con `array-contains`; mai
     `linkedProfiles` sul genitore, che è la faccia debole. */
  const figliPerTutore = useMemo(() => {
    const perTutore = new Map();

    users.forEach((figlio) => {
      const idFiglio = String((figlio && figlio.id) || "");
      if (!idFiglio) return;

      (Array.isArray(figlio.guardians) ? figlio.guardians : []).forEach(
        (idTutore) => {
          const chiave = String(idTutore || "");
          if (!chiave) return;
          if (!perTutore.has(chiave)) perTutore.set(chiave, []);
          perTutore.get(chiave).push(figlio);
        }
      );
    });

    return perTutore;
  }, [users]);

  /* IL CONFINE DELL'ANNATA (decisione del committente, 9/9/2026 — variante
     «b»). La riga famiglia NOMINA SOLO CHI È NELLA COORTE GUARDATA: un
     collegato non iscritto all'anno non ha riga propria in questa pagina, e
     nominarlo manderebbe a cercare qualcuno che la tabella non contiene.
     ⚠️ IL METRO È QUELLO DELLA PAGINA, non un secondo: si legge l'insieme già
     calcolato qui sopra, non si ricostruisce il criterio. */
  const idInCoorte = useMemo(
    () => new Set(coorteDiStagione.map((u) => String((u && u.id) || ""))),
    [coorteDiStagione]
  );

  /** Il nome a schermo di un collegato. Chi non ha né nome né cognome
   *  leggibili non si può nominare: si dice che c'è, non si inventa un nome. */
  const nomeDiPersona = (p) => {
    const nome = [p && p.firstName, p && p.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    return nome || "Unnamed member";
  };

  /** La squadra di un collegato nell'annata guardata. Sede unica
   *  `squadreDelSoggetto`: qui non nasce un terzo modo di leggere le tessere.
   *  ⚠️ `stati: null` — TUTTI gli stati di tessera, lo stesso metro della
   *  coorte di questa pagina, che è quella dove le PENDING si approvano. */
  const squadraDiPersona = (p) => {
    const { nomi, tutte } = squadreDelSoggetto(p?.contexts, stagioneAttiva, {
      stati: null
    });

    if (tutte) return "all teams";
    return nomi.length ? nomi.join(", ") : "no team";
  };

  /**
   * I GRUPPI DI FAMIGLIA DI UNA PERSONA, pronti da scrivere sotto il nome.
   *
   * Torna al massimo due righe — «Children: …» e «Parents: …» — ed elenco
   * vuoto quando non c'è niente da dire. Le due righe possono convivere: chi è
   * genitore di qualcuno ed è a sua volta figlio di qualcun altro le ha
   * entrambe, e non è un caso da nascondere.
   *
   * ⚠️ SI GUARDA IL DOCUMENTO, NON IL RUOLO MOSTRATO. Un documento PARENT con
   * una tessera ACTIVE di allenatore compare in questa pagina per la tessera;
   * i legami però stanno sul documento, ed è lì che si leggono — altrimenti la
   * riga famiglia sparirebbe proprio nel caso in cui serve.
   *
   * ⚠️ I FIGLI VENGONO DA DUE FONTI, e non è una ridondanza: l'indice inverso
   * (`guardians` sul figlio) è la faccia AUTOREVOLE; `linkedProfileIds` è la
   * faccia DEBOLE e si aggiunge solo per ciò che la prima non ha già dato.
   * Serve a rendere visibile il legame scritto a una faccia sola — difetto
   * noto delle scritture vecchie — che oggi non si vede da nessuna parte.
   *
   * ⚠️ ID-FANTASMA: `guardians` non viene MAI ripulito, quindi un id può non
   * corrispondere più ad alcun documento. Non si tace — si scrive il ripiego,
   * lo stesso testo di View Profile — perché è un difetto del dato che
   * l'amministrazione deve poter vedere. Chi invece SI RISOLVE ma sta fuori
   * dall'annata guardata resta muto: è la variante «b», scelta il 9/9/2026.
   */
  const famigliaDiPersona = (u) => {
    const idPersona = String((u && u.id) || "");

    /* Un gruppo per volta. I fantasmi non si ripetono in fila: uno solo porta
       il testo di View Profile alla lettera, più d'uno si conta. */
    const componi = (collegati, etichette) => {
      const nominati = [];
      let fantasmi = 0;

      collegati.forEach((persona) => {
        if (!persona) {
          fantasmi += 1;
          return;
        }
        if (!idInCoorte.has(String(persona.id || ""))) return;

        nominati.push(
          etichette.conSquadra
            ? `${nomeDiPersona(persona)} — ${squadraDiPersona(persona)}`
            : nomeDiPersona(persona)
        );
      });

      const voci = nominati.slice();
      if (fantasmi === 1) voci.push(etichette.ripiegoUno);
      else if (fantasmi > 1) voci.push(`${fantasmi} ${etichette.ripiegoTanti}`);

      if (!voci.length) return null;

      /* Il RIPIEGO DA SOLO NON VUOLE TITOLO: «Child on file — profile not
         available» si spiega già, e «Child: Child on file…» direbbe due volte
         la stessa parola. Il titolo torna appena c'è qualcuno da nominare. */
      if (!nominati.length) return voci.join(" · ");

      return `${voci.length > 1 ? etichette.titoloTanti : etichette.titoloUno}: ${voci.join(" · ")}`;
    };

    /* I FIGLI. La Map toglie da sé i doppioni — lo stesso id due volte fra le
       due fonti, o due volte dentro la stessa — e l'auto-riferimento (un
       documento che elenca sé stesso) si scarta: non è una famiglia. */
    const figli = new Map();

    (figliPerTutore.get(idPersona) || []).forEach((figlio) => {
      const chiave = String((figlio && figlio.id) || "");
      if (chiave && chiave !== idPersona) figli.set(chiave, figlio);
    });

    (Array.isArray(u && u.linkedProfileIds) ? u.linkedProfileIds : []).forEach(
      (id) => {
        const chiave = String(id || "");
        if (!chiave || chiave === idPersona || figli.has(chiave)) return;
        figli.set(chiave, indiceDiPersona.get(chiave) || null);
      }
    );

    /* I TUTORI, dal campo del soggetto: qui la faccia autorevole è già in mano
       e non serve alcun indice. */
    const tutori = new Map();

    (Array.isArray(u && u.guardians) ? u.guardians : []).forEach((id) => {
      const chiave = String(id || "");
      if (!chiave || chiave === idPersona || tutori.has(chiave)) return;
      tutori.set(chiave, indiceDiPersona.get(chiave) || null);
    });

    const righe = [];

    const rigaFigli = componi(Array.from(figli.values()), {
      titoloUno: "Child",
      titoloTanti: "Children",
      ripiegoUno: "Child on file — profile not available",
      ripiegoTanti: "children on file — profiles not available",
      conSquadra: true
    });
    if (rigaFigli) righe.push({ chiave: "children", testo: rigaFigli });

    /* ⚠️ Il titolo dice «Parent», il ripiego dice «Guardian»: la dissonanza è
       voluta. Il testo del ripiego è quello di View Profile ALLA LETTERA, e
       due grafie diverse della stessa mancanza sarebbero peggio di questa. */
    const rigaTutori = componi(Array.from(tutori.values()), {
      titoloUno: "Parent",
      titoloTanti: "Parents",
      ripiegoUno: "Guardian on file — profile not available",
      ripiegoTanti: "guardians on file — profiles not available",
      conSquadra: false
    });
    if (rigaTutori) righe.push({ chiave: "parents", testo: rigaTutori });

    return righe;
  };

  /* =======================
     LE VOCI DELLA TENDINA TEAM DEI FILTRI (Fetta A, lavoro L — decisione D4)

     Vengono dalla TABELLA, non dall'elenco delle squadre del club: sono le
     squadre che si trovano davvero sulle tessere dell'anno guardato. Offrire
     le squadre del club vorrebbe dire proporre voci che l'annata non può
     mostrare — si sceglie una squadra e l'elenco esce vuoto senza che nulla
     lo spieghi.

     ⚠️ SI COSTRUISCE DALLA COORTE, NON DA `rows`. `rows` è filtrata anche da
     `teamFilter`: costruire le voci da lì creerebbe un anello — scelta una
     squadra, resterebbe l'unica voce offerta e non si potrebbe più tornare
     indietro. La pagina gemella lo fa infatti dalla propria coorte.

     ⚠️ Questo NON tocca le squadre ASSEGNABILI (`teamNames`), che restano
     quelle del club: si filtra su ciò che c'è, si assegna fra ciò che
     esiste. Due domande diverse, due elenchi diversi.

     Doppioni tolti a confronto normalizzato, a schermo la prima grafia
     incontrata: «U12» e «u12» sono una voce sola, non due.
  ======================= */
  const squadreInTabella = useMemo(() => {
    const perChiave = new Map();

    coorteDiStagione.forEach((u) => {
      tessereDelSoggetto(u?.contexts, stagioneAttiva, {
        stati: null
      }).forEach((ctx) => {
        squadreDellaTessera(ctx).teams.forEach((nome) => {
          const chiave = normalizza(nome);
          if (chiave && !perChiave.has(chiave)) perChiave.set(chiave, nome);
        });
      });
    });

    return Array.from(perChiave.values()).sort((a, b) =>
      normalizza(a).localeCompare(normalizza(b))
    );
  }, [coorteDiStagione, stagioneAttiva]);

  /** Ricarica la sola anagrafica utenti (dopo scritture che la toccano). */
  async function ricaricaUtenti() {
    const refreshed = await api.get("/users");
    setUsers(Array.isArray(refreshed) ? refreshed : []);
  }

  /**
   * Riporta la tendina di stato al valore VERO del documento.
   *
   * ⚠️ La <select> è controllata da `u.status`, senza stato locale: in ogni
   * via che NON scrive la tendina resterebbe visivamente sul valore scelto
   * mentre il documento dice ancora l'altro. `ricaricaUtenti` riscrive la
   * lista dal backend e riporta la tendina al valore vero. È silenziosa di
   * proposito: è un ripristino di cortesia, non deve coprire con un proprio
   * errore il messaggio che conta.
   *
   * ⚠️ PERCHÉ QUI E NON DENTRO UN GESTO. `archiviaFamiglia` e
   * `attivaTessereDellAnno` se la tengono come chiusura locale, e va bene:
   * là il flusso sta tutto in una funzione. Il rientro dell'archiviato è
   * spezzato in tre pezzi (§C.3 del disegno) e ne ha bisogno in tutti e tre
   * — la lettura, la conferma e la chiusura della modale — quindi sale di
   * livello. Le due chiusure locali restano dove sono: toccarle sarebbe una
   * modifica a gesti che questa fetta non deve toccare.
   */
  function ripristinaTendina() {
    return ricaricaUtenti().catch(() => {});
  }

  /* =======================
     LOAD USERS + TEAMS

     ⚠️ LA LISTA DELLE SQUADRE SI RILEGGE AL CAMBIO D'ANNATA (teams-season,
     C10/N4). Le dipendenze erano `[]`: la lista si caricava una volta sola al
     montaggio e restava quella per tutta la sessione, quindi chi passava al
     2027 continuava a vedere — e ad ASSEGNARE — le squadre del 2026. Ora
     l'anno guardato e' fra le dipendenze, e la chiamata lo porta con se'.

     ⚠️ Il filtro «Filter by team» era GIA' per annata (viene dalle tessere
     della coorte, `squadreInTabella`): era la lista ASSEGNABILE
     (`teamNames`, costruita da questo stato) a non rileggersi mai.

     ⚠️ Si rilegge anche `/users`, ed e' il prezzo di un `useEffect` solo. E'
     lo stesso gesto che la pagina fa gia' da se' dopo ogni scrittura
     (`ricaricaUtenti`), e spezzare in due effetti vorrebbe dire reggere due
     stati di caricamento indipendenti per risparmiare una lettura su un
     cambio d'anno, che e' un gesto raro.

     ⚠️ Anno non calcolabile (`null`): la chiamata parte NUDA e il backend
     risponde con l'anno operativo. Su un dato che non si sa leggere non si fa
     sparire nessuno — la stessa regola che la pagina applica alle tessere.
  ======================= */
  useEffect(() => {
    async function loadAll() {
      try {
        setLoading(true);

        const percorsoTeams = Number.isInteger(stagioneAttiva)
          ? `/teams?season=${encodeURIComponent(stagioneAttiva)}`
          : "/teams";

        const usersData = await api.get("/users");
        const teamsData = await api.get(percorsoTeams);

        setUsers(Array.isArray(usersData) ? usersData : []);
        setTeams(mappaTeams(teamsData));
      } catch (err) {
        // R9.3: il 401 è la sessione scaduta, non un guasto di caricamento.
        setError(
          err?.status === 401 ? SESSION_EXPIRED_MESSAGE : "Failed to load data"
        );
      } finally {
        setLoading(false);
      }
    }

    loadAll();
  }, [stagioneAttiva]);

  /* =======================
     SCRITTURA DELLE SQUADRE — PER TESSERA (F3b)

     UNA SOLA PORTA per tutte le squadre di questa pagina:
     PUT /users/:userId/contexts/:tesseraId/teams, corpo { teams, allTeams }.
     Il bersaglio è la TESSERA, nominata dal suo codicino (F1), non la
     persona: è questo che chiude il baco «due ruoli → stessa squadra», dove
     il selettore di una riga scriveva `user.team` e la squadra compariva
     anche sull'altra riga della stessa persona.

     ⚠️ Da qui NON si scrive più `user.team`, e nessuna chiamata parte più
     verso PUT /team/:id o PUT /teamtd/:id: quelle due porte scrivono per
     PERSONA e non sanno dire di quale tessera si stia parlando.
  ======================= */

  /**
   * Scrive le squadre di UNA tessera e aggiorna la sola tessera in pagina.
   *
   * L'aggiornamento locale è per `id`: si sostituisce la tessera colpita
   * dentro `contexts`, e nient'altro. Mai un campo di primo livello della
   * persona — sarebbe di nuovo il baco. Quando la risposta porta la tessera
   * aggiornata la si prende così com'è (è il documento vero, coi vecchi campi
   * già ricalcolati dal backend); altrimenti si stende la sola coppia
   * `{ teams, allTeams }`, che è ciò che la pagina legge.
   */
  async function salvaSquadreTessera(userId, tesseraId, squadre) {
    const risposta = await api.put(
      `/users/${encodeURIComponent(userId)}/contexts/${encodeURIComponent(
        tesseraId
      )}/teams`,
      {
        teams: squadre.teams,
        allTeams: squadre.allTeams,
        // ⚠️ L'ANNO GUARDATO VIAGGIA COL GESTO. Il backend non ha più un
        // ripiego: senza `season` risponde 400. È l'anno di «Season Viewed»,
        // lo stesso che il bottone richiede per rendersi (`puoiAssegnare`) e
        // lo stesso che Approve e Archive mandano alle loro porte.
        season: stagioneAttiva
      }
    );

    const tesseraAggiornata = (risposta && risposta.context) || squadre;

    setUsers((prev) =>
      prev.map((u) => {
        if (String(u.id) !== String(userId)) return u;

        const contexts = Array.isArray(u.contexts) ? u.contexts : [];

        return {
          ...u,
          contexts: contexts.map((ctx) =>
            ctx && String(ctx.id) === String(tesseraId)
              ? { ...ctx, ...tesseraAggiornata }
              : ctx
          )
        };
      })
    );
  }

  /* =======================
     TEAM TD APPROVED TEAMS
  ======================= */
  function openTeamTDModal(u, tessera) {
    // Le squadre di partenza sono quelle DELLA TESSERA che ha aperto la
    // finestra — non «la tessera coach della persona», che con due tessere
    // non è un dato ma una scommessa. La forma è già quella nuova
    // (`teams`/`allTeams`): la spunta la detta `allTeams`, e l'elenco
    // esplicito non contiene la sentinella per costruzione.
    const squadre = squadreDellaTessera(tessera);

    // ⚠️ FETTA A (lavoro K): la finestra si apre ora anche su una tessera NON
    // allenatore, e lì «tutte le squadre» non esiste — su un non-coach quel
    // permesso si perde in silenzio, e la modale a scelta singola non lo
    // mostra nemmeno. Si parte quindi da uno stato coerente con ciò che si
    // vedrà: spunta spenta e una sola squadra, la prima (che è la stessa che
    // il backend scrive nei vecchi campi, regola A ramo non-coach).
    const eTesseraCoach = canonicalRole(tessera?.role) === "COACH";

    setTeamTDUser(u);
    setTeamTDTessera(tessera);
    setTutteLeSquadre(eTesseraCoach ? squadre.allTeams : false);
    setSelectedTeams(
      eTesseraCoach ? squadre.teams : squadre.teams.slice(0, 1)
    );
    // La ricerca riparte vuota a ogni apertura: la finestra si apre sempre
    // sull'elenco INTERO, e il testo cercato per un'altra persona non si
    // trascina dietro nascondendo squadre senza che si veda perché.
    setTeamSearchQuery("");
  }

  function chiudiTeamTDModal() {
    setTeamTDUser(null);
    setTeamTDTessera(null);
    setSelectedTeams([]);
    setTutteLeSquadre(false);
    setTeamSearchQuery("");
  }

  /**
   * La tessera aperta nella modale è di un ALLENATORE? Da questo dipende la
   * sola resa della finestra (lavoro K): N spunte più «All teams» per il
   * coach, scelta singola e nessuna sentinella per gli altri. La scrittura è
   * la stessa per tutti e due, e lo era già.
   */
  const tesseraDelCoach = canonicalRole(teamTDTessera?.role) === "COACH";

  function toggleApprovedTeam(team) {
    // SCELTA SINGOLA per i non allenatori: la spunta nuova SOSTITUISCE la
    // precedente, e ri-spuntare quella accesa lascia la tessera senza
    // squadra. «Nessuna squadra» è l'elenco VUOTO, non una voce vuota
    // nell'elenco — è lo stesso contratto della tendina che questa modale
    // sostituisce.
    if (!tesseraDelCoach) {
      setSelectedTeams((prev) => (prev.includes(team) ? [] : [team]));
      return;
    }

    setSelectedTeams((prev) =>
      prev.includes(team)
        ? prev.filter((t) => t !== team)
        : [...prev, team]
    );
  }

  async function saveApprovedTeams() {
    if (!teamTDUser || !teamTDTessera?.id) return;

    try {
      // O «tutte le squadre», o l'elenco esplicito: mai i due insieme (doc 07
      // §8.1, rev. 6/8/2026). Da F3b la cosa la dicono i due campi del
      // modello B, non più la sentinella dentro l'elenco: con `allTeams` vero
      // l'elenco parte VUOTO, ed è il backend a ricavare i vecchi campi.
      //
      // ⚠️ FETTA A: su una tessera NON allenatore la sentinella non parte
      // mai. La spunta non si mostra e l'apertura la spegne, ma il valore si
      // ricalcola anche qui: se una tessera storica non-coach arrivasse con
      // `allTeams` vero, salvare senza questa guardia riscriverebbe il
      // permesso pieno mentre a schermo si stava scegliendo UNA squadra.
      const tutte = tesseraDelCoach && tutteLeSquadre;

      await salvaSquadreTessera(teamTDUser.id, teamTDTessera.id, {
        teams: tutte ? [] : selectedTeams,
        allTeams: tutte
      });

      chiudiTeamTDModal();
    } catch (err) {
      // ⚠️ R9.3: su questa rotta il 409 NON è «squadra già esistente» ma la
      // stagione illeggibile, e i 400 (squadra sconosciuta nell'annata, un
      // giocatore in una sola squadra…) li spiega il server in inglese: si
      // mostra il suo testo. 401, 403 e 404 restano a `messaggioErrore`.
      if (err?.status === 409 || err?.status === 400) {
        alert(
          motivoDalServer(err) ||
            "The team assignment was not saved. Please check the selection and try again."
        );
      } else {
        alert(messaggioErrore(err, "Failed to update approved teams"));
      }
    }
  }

  /* =======================
     UPDATE TEAM / STATUS
  ======================= */
  /*
   * ⛔️ RIMOSSA nella Fetta A (lavoro K) — `changeTeam(userId, tesseraId,
   * newTeam)`, il selettore in linea della riga NON allenatore.
   *
   * Non c'è più una tendina in linea: la squadra di un giocatore si sceglie
   * nella STESSA modale dell'allenatore, a scelta singola, e il salvataggio
   * passa da `saveApprovedTeams` — che è sempre stata la stessa scrittura
   * (`salvaSquadreTessera`, stessa rotta per-tessera, stesso payload
   * `{ teams, allTeams }`). Cade la funzione, non la porta.
   *
   * ⚠️ Resta intatto il contratto che questa funzione dichiarava: assegnare
   * la squadra NON cambia lo stato — un PENDING che riceve la squadra resta
   * PENDING, nessuna promozione implicita. Nemmeno la modale lo tocca.
   */

  // ⚠️ PORTA VIVA MA SENZA CHIAMANTI DA QUESTA PAGINA (Fetta B).
  // `changeStatus` non ha più chiamanti qui — la tendina non offre più PENDING
  // (commit e002496) e ACTIVE passa da `attivaTessereDellAnno`, che porta con
  // sé l'anno guardato — ma resta la sola porta del ramo PENDING del backend,
  // dichiarata inerte. Non manda alcun `season`, e va bene così: il ramo
  // PENDING del server non tocca le tessere e non guarda annate. Rimuoverla è
  // una micro-fetta a parte, non questa: qui si lascia invariata, riga per
  // riga.
  async function changeStatus(userId, newStatus) {
    // Lo stato vuoto (documento senza `status`) non si scrive mai.
    if (!newStatus) return;

    try {
      await api.put(`/users/${userId}/status`, {
        status: newStatus
      });

      // ricarica utenti dal backend
      const refreshed = await api.get("/users");
      setUsers(Array.isArray(refreshed) ? refreshed : []);
    } catch (err) {
      alert(messaggioErrore(err, "Failed to update user status"));
    }
  }

  // ARCHIVIAZIONE DI FAMIGLIA (cantiere «archiviazione-famiglia», Fetta 3).
  //
  // ⚠️ La tendina di stato NON passa da `changeStatus` quando il valore scelto
  // è ARCHIVED. Archiviare non è più «scrivere uno stato»: è un gesto di
  // FAMIGLIA, che può portarsi dietro i figli rimasti senza alcun tutore
  // valido. Il backend lo sa e si difende — PUT /users/:id/status ARCHIVED
  // rifiuta con 409 CONFIRM_REQUIRED se il corpo non porta `expectedChildren`
  // (server.js) — quindi mandare il solo `{ status }`, come fa `changeStatus`,
  // oggi non archivierebbe nulla.
  //
  // ⚠️ Da allora la stessa cosa è successa ad ACTIVE: dalla Fetta B anche
  // l'attivazione ha la sua via (`attivaTessereDellAnno`), perché anche lei
  // deve portare con sé l'anno guardato. A `changeStatus` resta il solo
  // PENDING, che nessuno di questa pagina chiama più.
  //
  // Il giro è: ANTEPRIMA (chi resterebbe scoperto) → CONFERMA FORTE se c'è
  // qualcuno → ARCHIVIAZIONE con il numero firmato. Il numero torna al
  // server, che lo ricontrolla contro il proprio ricalcolo DENTRO il
  // lucchetto: se nel frattempo la famiglia è cambiata, 409 FAMILY_CHANGED e
  // la decisione su un numero diverso torna all'essere umano.
  //
  // ⚠️ L'ANNO GUARDATO VIAGGIA CON IL GESTO, in tutte e due le chiamate.
  // Archiviare non spegne più tutte le tessere di una persona: spegne quelle
  // dell'ANNATA che si sta guardando, e quale sia lo sa solo il client. Il
  // backend NON ripiega sull'anno operativo se l'anno manca — rifiuta con 400 —
  // proprio perché un ripiego raderebbe le tessere di un anno che
  // l'amministratore non stava guardando.
  //
  // ⚠️ Anteprima e scrittura mandano lo STESSO valore, letto una volta sola:
  // due letture diverse farebbero promettere un numero e scriverne un altro.
  async function archiviaFamiglia(userId) {
    // ⚠️ La <select> è controllata da `u.status`, senza stato locale: in ogni
    // via che NON archivia la tendina resterebbe visivamente su ARCHIVED
    // mentre il documento dice ancora ACTIVE. `ricaricaUtenti` riscrive la
    // lista dal backend e riporta la tendina al valore vero. È silenziosa di
    // proposito: è un ripristino di cortesia, non deve coprire con un proprio
    // errore il messaggio che conta.
    const ripristina = () => ricaricaUtenti().catch(() => {});

    // ⚠️ ANNO IGNOTO → NON SI PARTE. `stagioneAttiva` vale `null` nel solo caso
    // in cui il default d'annata non è calcolabile; lì la regola dell'anno si
    // autoesclude e la lista mostra tutti, quindi la persona compare comunque
    // e la tendina è viva. Senza questa guardia ogni archiviazione morirebbe
    // sul 400 del backend con la pagina d'aspetto perfettamente normale.
    if (stagioneAttiva === null) {
      window.alert("Select a season before archiving.");
      await ripristina();
      return;
    }

    let count = 0;

    try {
      const anteprima = await api.get(
        `/users/${userId}/archive-preview?season=${stagioneAttiva}`
      );
      count = Number.isInteger(anteprima?.count) ? anteprima.count : 0;
    } catch (err) {
      alert("Failed to check this member's family before archiving");
      await ripristina();
      return;
    }

    // Nessun figlio-scoperto: si archivia senza domande, come si è sempre
    // fatto. Il numero si manda lo stesso — zero è una risposta, non un
    // silenzio: senza, il backend non distinguerebbe «me ne ha detti zero» da
    // «non me l'ha detto» e rifiuterebbe con 409.
    if (count > 0) {
      // ⚠️ Stessa forma della conferma scritta del ritiro stagione
      // (SeasonManagement.jsx) e del DELETE di un membro: una domanda
      // secca, poi la parola da digitare per esteso, confronto SECCO e
      // maiuscolo. Non si inventa una modale nuova per un gesto distruttivo:
      // nell'app la si chiede così.
      // ⚠️ L'ANNO SI DICE. La domanda parlava di «archiviare questo membro»
      // senza mai nominare l'annata, mentre il gesto tocca le tessere del
      // SOLO anno guardato: chi conferma deve leggere quale. Solo il testo:
      // il confronto secco della doppia conferma resta identico.
      const proseguo = window.confirm(
        `Archiving this member for ${stagioneAttiva} will also archive ` +
          `${count} child membership(s) for the same season that would be ` +
          "left without an active guardian. This cannot be undone."
      );

      if (!proseguo) {
        await ripristina();
        return;
      }

      const doppia = window.prompt('Type "ARCHIVE" to confirm:');

      if (doppia !== "ARCHIVE") {
        window.alert("Cancelled");
        await ripristina();
        return;
      }
    }

    try {
      await api.put(`/users/${userId}/status`, {
        status: "ARCHIVED",
        expectedChildren: count,
        season: stagioneAttiva
      });
    } catch (err) {
      // ⚠️ I due 409 hanno un messaggio PROPRIO. `messaggioErrore` è la voce
      // della modale squadre: passandogli questi errori l'amministratore si
      // vedrebbe rispondere di squadre mentre sta archiviando una famiglia.
      if (err?.status === 409) {
        let codice = "";
        let contoNuovo = null;

        try {
          const corpo = JSON.parse(err.body);
          codice = corpo?.code || "";
          contoNuovo = Number.isInteger(corpo?.count) ? corpo.count : null;
        } catch (e) {
          // Corpo non leggibile: resta il messaggio generico qui sotto.
        }

        if (codice === "FAMILY_CHANGED") {
          window.alert(
            `The family changed while you were confirming (now ${
              contoNuovo ?? "a different number of"
            } children). Please review and try again.`
          );
        } else if (codice === "CONFIRM_REQUIRED") {
          window.alert("This action needs confirmation.");
        } else {
          // R9.3: il 409 senza codice è la stagione illeggibile: lo dice il
          // server.
          window.alert(motivoDalServer(err) || "Failed to archive user");
        }
      } else if (err?.status === 401) {
        // R9.3: sessione scaduta, come in Approve e Readmit (D8B, RD-166).
        window.alert(SESSION_EXPIRED_MESSAGE);
      } else {
        window.alert("Failed to archive user");
      }

      await ripristina();
      return;
    }

    // Archiviato: la lista va comunque riletta, perché questa scrittura ha
    // toccato anche i figli.
    await ripristina();
  }

  // ATTIVAZIONE PER-ANNATA (cantiere «Team Assignment», Fetta B).
  //
  // ⚠️ Gemella di `archiviaFamiglia`, di verso opposto: quella SPEGNE le
  // tessere dell'anno guardato e ne azzera le squadre; questa ACCENDE le
  // tessere PENDING dello stesso anno e NON tocca nient'altro. La tessera
  // resta SPOGLIA — nessuna squadra assegnata, nessuna azzerata. Dare la
  // squadra è un gesto suo, dalla modale.
  //
  // ⚠️ L'ANNO GUARDATO VIAGGIA CON IL GESTO. Fino alla Fetta B l'attivazione
  // passava da `changeStatus`, che mandava il solo `{ status }`: il backend
  // sceglieva le tessere con l'anno OPERATIVO e, guardando un'altra annata, il
  // gesto cadeva nel vuoto — la persona diventava ACTIVE, la tessera mostrata
  // restava PENDING, la riga tornava identica. Ora l'annata la dice il client,
  // e il backend RIFIUTA con 400 se non arriva: nessun ripiego, perché
  // accendere la tessera di un anno che l'amministratore non stava guardando
  // concede ruoli e squadre a chi non li ha chiesti.
  //
  // ⚠️ NUOVI E DI RITORNO SONO LO STESSO GESTO. Non c'è un ramo che distingua
  // «Approve» da «Renew»: cambia l'etichetta del bottone, non il codice. Si
  // accende qualunque tessera PENDING dell'anno guardato, indifferentemente.
  //
  // ⚠️ LO STATUS DI PRIMO LIVELLO NON LO DECIDE QUESTA CHIAMATA: lo deriva lo
  // specchio del backend, che guarda l'anno OPERATIVO. Chi è ARCHIVED o
  // PENDING nell'anno operativo resta tale anche dopo che gli si è accesa la
  // tessera di un'altra annata. È atteso e dichiarato: il rientro
  // dell'archiviato è un lavoro a parte (Coda 4), non questa fetta.
  async function attivaTessereDellAnno(userId) {
    // Stessa meccanica di ripristino di `archiviaFamiglia`: la <select> è
    // controllata da `u.status`, senza stato locale, quindi in ogni via che
    // NON scrive va riportata al valore vero rileggendo la lista. È silenziosa
    // di proposito: è un ripristino di cortesia, non deve coprire con un
    // proprio errore il messaggio che conta.
    const ripristina = () => ricaricaUtenti().catch(() => {});

    // ⚠️ ANNO IGNOTO → NON SI PARTE. `stagioneAttiva` vale `null` nel solo caso
    // in cui il default d'annata non è calcolabile; lì la regola dell'anno si
    // autoesclude e la lista mostra tutti, quindi la persona compare comunque
    // e il bottone è vivo. Senza questa guardia ogni attivazione morirebbe sul
    // 400 del backend con la pagina d'aspetto perfettamente normale.
    if (stagioneAttiva === null) {
      window.alert("Select a season before approving.");
      await ripristina();
      return;
    }

    try {
      await api.put(`/users/${userId}/status`, {
        status: "ACTIVE",
        season: stagioneAttiva
      });
    } catch (err) {
      // ⚠️ IL MOTIVO DEL RIFIUTO ARRIVA A CHI HA PREMUTO. `messaggioErrore` è
      // la voce della modale squadre: passandogli questi errori l'ammini-
      // stratore si vedrebbe rispondere di squadre mentre sta approvando una
      // persona. Il 400 lo spiega il server (annata assente o malformata); il
      // 403 è sempre la stessa cosa e si dice a parole nostre; il 401 è la
      // sessione scaduta (D8B, RD-166).
      if (err?.status === 401) {
        window.alert(SESSION_EXPIRED_MESSAGE);
      } else if (err?.status === 403) {
        window.alert("You don't have permission to do this.");
      } else if (err?.status === 400) {
        let motivo = "";

        try {
          const corpo = JSON.parse(err.body);
          motivo = [corpo?.error, corpo?.detail].filter(Boolean).join(": ");
        } catch (e) {
          // Corpo non leggibile: resta il messaggio generico qui sotto.
        }

        window.alert(motivo || "Failed to approve this member");
      } else if (err?.status === 409) {
        // R9.3: la stagione illeggibile, col testo del server.
        window.alert(motivoDalServer(err) || "Failed to approve this member");
      } else {
        window.alert("Failed to approve this member");
      }

      await ripristina();
      return;
    }

    // Acceso: la lista si rilegge, come faceva `changeStatus`.
    await ripristina();
  }

  /** Il nome della persona, per le frasi del rientro. Si legge dalla lista già
   *  in mano — la stessa che regge le righe — senza chiamate nuove: chi ha
   *  aperto la tendina è per forza una riga di questa pagina. */
  function nomeDiUtente(userId) {
    const u = users.find((persona) => persona.id === userId);

    if (!u) return "";

    return `${u.firstName || ""} ${u.lastName || ""}`.trim();
  }

  // ===============================
  // IL RIENTRO DELL'ARCHIVIATO (Coda 4, fetta R4) — TRE PEZZI
  // ===============================
  //
  // ⚠️ Gemello di `archiviaFamiglia`, di verso opposto: quella SPEGNE le
  // tessere dell'anno guardato e può portarsi dietro i figli; questo fa
  // RINASCERE le tessere — PENDING, in anticamera — nell'anno OPERATIVO, e può
  // riportare con sé i figli che l'amministratore sceglie uno per uno.
  //
  // ⚠️ DUE ANNI DIVERSI, E NON VANNO CONFUSI. `archiviaFamiglia` e
  // `attivaTessereDellAnno` guardano `stagioneAttiva`, l'anno GUARDATO, perché
  // spengono e accendono tessere di quell'annata. Il rientro guarda
  // `operatingSeason`, l'anno OPERATIVO, perché è là che una tessera nuova
  // nasce: riammettere qualcuno «nel 2025» perché l'admin sta guardando il 2025
  // lo iscriverebbe a una stagione finita.
  //
  // ⚠️ IL GESTO NON SCRIVE NULLA DA SÉ: chiama l'anteprima
  // (`GET /users/:id/reentry-preview`, fetta R1) e la scrittura
  // (`POST /users/:id/reentry`, fetta R3). Il calcolo di CHI rinasce e la
  // difesa dei minori stanno nel backend, sotto lucchetto; qui c'è la scelta
  // consapevole dell'essere umano e il modo di raccontargliela.

  /**
   * PEZZO 1 — legge l'anteprima, decide, e o conclude o APRE LA MODALE.
   *
   * ⚠️ Con figli da mostrare questa funzione FINISCE all'apertura della
   * finestra: il resto vive in `confermaRientro`. Non c'è alcun `await` che
   * attenda la modale — non sarebbe attendibile (vedi la nota sugli stati).
   */
  async function riammettiFamiglia(userId) {
    // ⚠️ ANNO OPERATIVO IGNOTO → NON SI PARTE. Gemella della guardia d'anno di
    // `archiviaFamiglia`, ma su un'ALTRA annata: qui `stagioneAttiva` non
    // c'entra. Senza questa guardia il gesto morirebbe sul 409 del backend
    // («anno operativo illeggibile») con la pagina d'aspetto normale.
    if (operatingSeason == null) {
      window.alert("The club season is unavailable. Try again in a moment.");
      await ripristinaTendina();
      return;
    }

    let anteprima;

    try {
      anteprima = await api.get(`/users/${userId}/reentry-preview`);
    } catch (err) {
      window.alert("Failed to load this member's family");
      await ripristinaTendina();
      return;
    }

    // ⚠️ RIFIUTO PRECOCE. Se sul bersaglio non rinascerebbe nulla, il gesto si
    // ferma QUI: niente modale, niente parola da digitare. Senza, il percorso
    // dell'amministratore sarebbe «carica → spunta i figli → conferma → digita
    // READMIT → 409»: si chiede molto per non concedere niente. La cura è la
    // stessa che il file già usa sulla voce ARCHIVED spenta quando l'anno
    // manca («meglio non offrirlo affatto che farlo partire e fermarlo»); qui
    // la voce non si può spegnere — un archiviato vero e uno senza tessere
    // leggibili sono indistinguibili da `u.status` — quindi si chiede al
    // server e si rifiuta prima di chiedere qualunque cosa all'admin.
    //
    // ⚠️ Il 409 del backend RESTA, e non è ridondante: questo è indicativo,
    // quello è la decisione autorevole presa sotto lucchetto.
    if (anteprima?.target?.readmittable === false) {
      window.alert(NULLA_DA_RIAMMETTERE);
      await ripristinaTendina();
      return;
    }

    const figli = Array.isArray(anteprima?.children) ? anteprima.children : [];
    const count = Number.isInteger(anteprima?.count) ? anteprima.count : figli.length;

    // L'anno che si NOMINA è quello che il server ha dichiarato: è lui a
    // decidere in quale stagione la tessera nasce, e dirne un altro sarebbe
    // una promessa che non gli appartiene. Se tacesse resta quello del
    // contesto, che la guardia qui sopra ha già trovato buono — mai un
    // «undefined season» a schermo.
    const anno = anteprima?.operatingSeason ?? operatingSeason;

    // NESSUN FIGLIO: non c'è nulla da scegliere, quindi non si apre finestra.
    // Resta la conferma singola — la stessa asimmetria di `archiviaFamiglia`,
    // che con `count === 0` non chiede nulla — ma qui la domanda c'è comunque,
    // e la parola pure, perché il gesto è altrimenti INVISIBILE nell'anno
    // guardato (vedi l'avviso di esito in fondo a `confermaRientro`).
    if (count === 0) {
      const proseguo = window.confirm(
        `Readmit this member for the ${anno} season` +
          `${frasePerRuoli(anteprima?.target?.roles)}? ` +
          "They will return as PENDING and will need to be approved."
      );

      if (!proseguo) {
        await ripristinaTendina();
        return;
      }

      const doppia = window.prompt('Type "READMIT" to confirm:');

      if (doppia !== "READMIT") {
        window.alert("Cancelled");
        await ripristinaTendina();
        return;
      }

      await eseguiRientro(userId, anno, [], count);
      return;
    }

    // CON FIGLI: si apre la finestra e QUI LA FUNZIONE FINISCE.
    // ⚠️ Da questo momento la tendina mostra già ACTIVE mentre il documento
    // dice ancora ARCHIVED: è transitorio e accettabile — la finestra copre la
    // pagina, come già fa quella delle squadre — e il ripristino avviene alla
    // CHIUSURA, non all'apertura.
    setRientroModale({
      userId,
      nome: nomeDiUtente(userId),
      operatingSeason: anno,
      target: anteprima.target || null,
      count,
      children: figli
    });
    setFigliScelti([]);
  }

  /** La spunta di UN figlio: aggiunge o toglie il suo id dall'elenco scelto. */
  function toggleFiglio(idFiglio) {
    setFigliScelti((prev) =>
      prev.includes(idFiglio)
        ? prev.filter((id) => id !== idFiglio)
        : [...prev, idFiglio]
    );
  }

  /** Chiude la finestra e AZZERA la scelta.
   *
   *  ⚠️ Ripristina anche la tendina, e qui sta la differenza col gemello
   *  `chiudiTeamTDModal`, che non ripristina nulla: quella finestra non è
   *  comandata da una <select>, questa sì. Annullare è una via che NON
   *  riammette, e ogni via che non riammette riporta la tendina al vero. */
  function chiudiRientroModale() {
    setRientroModale(null);
    setFigliScelti([]);
    ripristinaTendina();
  }

  /**
   * PEZZO 2 — l'handler del bottone «Readmit» della finestra: doppia
   * conferma, poi la scrittura.
   */
  async function confermaRientro() {
    if (!rientroModale) return;

    const { userId, operatingSeason: anno, target, count } = rientroModale;
    // Solo i figli DAVVERO in elenco: se fra l'apertura e il Readmit lo stato
    // portasse un id che l'elenco non contiene più, non lo si manderebbe al
    // server insieme al numero firmato.
    const scelti = figliScelti.filter((id) =>
      rientroModale.children.some((f) => f.id === id)
    );

    // ⚠️ Stessa forma della doppia conferma di `archiviaFamiglia`: una domanda
    // secca che NOMINA L'ANNO e i ruoli, poi la parola da digitare per esteso,
    // confronto SECCO e maiuscolo. Non si inventa una forma nuova: nell'app un
    // gesto di famiglia si chiede così. Vale anche qui, benché il rientro non
    // distrugga nulla, perché SCRIVE SU DOCUMENTI DI MINORI.
    const proseguo = window.confirm(
      `Readmit this member for the ${anno} season` +
        `${frasePerRuoli(target?.roles)}? ` +
        `${scelti.length} selected child profile(s) will return as PENDING ` +
        "for the same season."
    );

    if (!proseguo) {
      // ⚠️ La finestra RESTA APERTA: annullare la domanda è un ripensamento
      // sulla conferma, non sulla selezione, e chiudere tutto costringerebbe a
      // rifare le spunte. Il ripristino della tendina arriva alla chiusura.
      return;
    }

    const doppia = window.prompt('Type "READMIT" to confirm:');

    if (doppia !== "READMIT") {
      window.alert("Cancelled");
      return;
    }

    await eseguiRientro(userId, anno, scelti, count);
  }

  /**
   * La sola chiamata che SCRIVE, più lo smistamento dei rifiuti e l'avviso di
   * esito. Vive a parte perché la fanno due vie diverse — quella con figli
   * (dalla finestra) e quella senza (che finestra non ne apre) — e una
   * scrittura sui minori non si scrive due volte.
   *
   * `anno` è l'anno OPERATIVO dichiarato dall'anteprima: arriva già deciso da
   * chi chiama, così le due vie nominano la stessa stagione nella conferma e
   * nell'avviso di esito.
   */
  async function eseguiRientro(userId, anno, figliScelti_, count) {
    try {
      await api.post(`/users/${userId}/reentry`, {
        children: figliScelti_,
        // Il numero firmato: è l'INTERO elenco visto, non i soli spuntati. È
        // su quell'elenco che si firma il patto — il server lo ricontrolla
        // contro il proprio ricalcolo dentro il lucchetto, e se la famiglia è
        // cambiata la decisione torna all'essere umano con un 409.
        // Zero è una risposta, non un silenzio: senza, il server non
        // distinguerebbe «me ne ha detti zero» da «non me l'ha detto».
        expectedChildren: count
      });
    } catch (err) {
      // ⚠️ MESSAGGI PROPRI, mai `messaggioErrore`: quella è la voce della
      // modale squadre, e passandole questi errori l'amministratore si vedrebbe
      // rispondere di squadre mentre sta riammettendo una famiglia.
      let codice = "";
      let motivo = "";
      let contoNuovo = null;

      try {
        const corpo = JSON.parse(err.body);
        codice = corpo?.code || "";
        motivo = corpo?.error || "";
        contoNuovo = Number.isInteger(corpo?.count) ? corpo.count : null;
      } catch (e) {
        // Corpo non leggibile: restano i messaggi generici qui sotto.
      }

      if (err?.status === 409 && codice === "NOTHING_TO_READMIT") {
        // Stessa identica frase del rifiuto precoce: è lo stesso fatto, letto
        // una volta in anticipo e una volta sotto lucchetto.
        window.alert(NULLA_DA_RIAMMETTERE);
      } else if (err?.status === 409 && codice === "FAMILY_CHANGED") {
        window.alert(
          `The family changed while you were confirming (now ${
            contoNuovo ?? "a different number of"
          } children). Please review and try again.`
        );
      } else if (err?.status === 409 && codice === "CONFIRM_REQUIRED") {
        window.alert("This action needs confirmation.");
      } else if (
        err?.status === 403 &&
        motivo === "You can only readmit this member's own children"
      ) {
        // ⚠️ QUESTO 403 NON È UN ERRORE DI PERMESSI, ed è l'unico che va letto
        // dal testo. Il backend controlla «è davvero figlio di questo genitore»
        // PRIMA del patto del numero: se un figlio spuntato viene sganciato fra
        // l'anteprima e il Readmit, la corsa esce di qui e non dal 409
        // FAMILY_CHANGED che la descriverebbe. Dire «non hai i permessi» a un
        // amministratore che i permessi ce li ha lo manderebbe a cercare il
        // problema dalla parte sbagliata: la cosa da fare è ricaricare.
        window.alert("The family may have changed. Please reload and try again.");
      } else if (err?.status === 401) {
        window.alert(SESSION_EXPIRED_MESSAGE);
      } else if (err?.status === 403) {
        window.alert("You don't have permission to do this.");
      } else if (err?.status === 400) {
        window.alert(motivo || "Failed to readmit this member");
      } else {
        window.alert("Failed to readmit this member");
      }

      // Anche il rifiuto è una via che non riammette: la finestra si chiude e
      // la tendina torna al vero.
      setRientroModale(null);
      setFigliScelti([]);
      await ripristinaTendina();
      return;
    }

    setRientroModale(null);
    setFigliScelti([]);
    await ripristinaTendina();

    // ⚠️ L'ESITO È INVISIBILE SE L'ADMIN GUARDA UN ALTRO ANNO, ed è il fatto
    // più importante di questo gesto. L'archiviato compare, di norma, nella
    // coorte dell'anno in cui FU ARCHIVIATO, spesso diverso dall'anno
    // operativo. Guardando il 2025 dopo un rientro nel 2026: la tessera 2026
    // non si vede (le righe filtrano per `stagioneAttiva`), il colore della
    // fila resta rosso (viene dalle tessere del 2025, ancora ARCHIVED), il
    // bottone Approve non compare (`daApprovare` guarda le PENDING dell'anno
    // guardato). Cambia solo il valore della tendina. Senza questo avviso
    // l'amministratore vedrebbe una fila ancora rossa e concluderebbe che il
    // gesto è fallito.
    //
    // ⚠️ LA VISTA NON SALTA. Non si tocca «Season Viewed»: l'anno guardato è
    // una scelta dell'amministratore e il gesto non deve rubargliela. Si dice
    // dove guardare, non si guarda al posto suo.
    const nome = nomeDiUtente(userId) || "This member";

    window.alert(
      `${nome} has been readmitted for the ${anno} season and is now PENDING. ` +
        `${figliScelti_.length} child profile(s) returned with them. ` +
        `Switch Season Viewed to ${anno} to approve them.`
    );
  }

  /**
   * L'onChange della tendina di stato. Smista e basta: ARCHIVED ha la sua via
   * (`archiviaFamiglia`), ACTIVE ne ha DUE (`attivaTessereDellAnno` o
   * `riammettiFamiglia`). Le prime due portano con sé l'ANNO GUARDATO: la
   * tendina segue la stessa annata del bottone, altrimenti due comandi
   * affiancati farebbero due cose diverse. La terza no: il rientro avviene
   * nell'anno OPERATIVO, e se lo legge da sé.
   *
   * ⚠️ ACTIVE SU UN ARCHIVIATO NON È «ATTIVA LE TESSERE», È UN RIENTRO (Coda 4).
   * Sono due gesti diversi sotto la stessa voce, e a distinguerli serve lo
   * STATO CORRENTE, che lo smistatore non aveva: arriva ora come terzo
   * argomento dal punto di chiamata, dove il dato è già in mano
   * (`const stato = normalizza(u.status)`). Nessuna lettura nuova, e lo
   * smistatore resta uno smistatore puro.
   *
   * ⚠️ IL METRO È `u.status`, cioè il riflesso dell'anno OPERATIVO — ed è
   * quello giusto, perché è nell'anno operativo che il rientro avviene. Una
   * persona con le tessere 2025 tutte ARCHIVED ma ACTIVE nel 2026 NON è un
   * archiviato da riammettere: `u.status` lo dice correttamente, il colore
   * della fila no — e dalla fetta RENEW-01 nemmeno il valore MOSTRATO dalla
   * tendina, che ora viene anch'esso dalle tessere dell'anno GUARDATO. È la
   * ragione per cui il terzo argomento resta `stato` e non `statoMostrato`.
   */
  function cambiaStatoDallaTendina(userId, nuovoStato, statoCorrente) {
    if (normalizza(nuovoStato) === "ARCHIVED") {
      archiviaFamiglia(userId);
      return;
    }

    if (normalizza(nuovoStato) === "ACTIVE") {
      if (normalizza(statoCorrente) === "ARCHIVED") {
        riammettiFamiglia(userId);
        return;
      }

      attivaTessereDellAnno(userId);
      return;
    }

    changeStatus(userId, nuovoStato);
  }

  /* =======================
     DERIVED ROWS (User × Context)
  ======================= */
  const rows = useMemo(() => {
    const result = [];

    // ⚠️ FETTA A — CAMBIA L'ASSE DEL FILTRO, non la costruzione della riga.
    // Prima: le tessere ACTIVE, di qualunque annata. Ora: le tessere
    // DELL'ANNO GUARDATO, in qualunque stato. `key`, `squadre` e
    // `displayRole` erano già per-tessera e restano identici: cambia solo
    // QUALI tessere arrivano fin qui.
    // La base è la COORTE d'annata, non più `users` integrale: chi non ha
    // tessere dell'anno (e non è amministratore) non compare affatto.
    coorteDiStagione.forEach((u) => {
      const tessereDiQuestAnno = tessereDelSoggetto(
        u?.contexts,
        stagioneAttiva,
        { stati: null }
      );

      if (tessereDiQuestAnno.length === 0) {
        // RIGA BASE. Compariva per «nessuna tessera ACTIVE»; dalla Fetta A
        // compare per «nessuna tessera DELL'ANNO GUARDATO». Con la coorte
        // dell'annata i soli a caderci sono gli AMMINISTRATORI senza tessera
        // d'anno, che entrano per eccezione: per loro vale la regola F10 già
        // in vigore — cella Team vuota PER RUOLO, non il testo.
        result.push({
          key: `${u.id}-base`,
          user: u,
          context: null,
          // Nessuna tessera, nessuna squadra: questa riga non ha un dato
          // per-tessera da mostrare né un bersaglio da scrivere.
          squadre: { teams: [], allTeams: false },
          displayRole: u.role,
        });
      } else {
        tessereDiQuestAnno.forEach((ctx, i) => {
          result.push({
            // La chiave è il CODICINO della tessera (F1) quando c'è: è il suo
            // nome proprio, e non cambia quando cambiano le squadre. Le
            // tessere storiche, nate senza codicino, tengono la chiave
            // composta di prima.
            key: `${u.id}-${ctx.id || `${ctx.role}-${ctx.team}-${i}`}`,
            user: u,
            context: ctx,
            // Le squadre DI QUESTA TESSERA: sono queste che la riga mostra e
            // che il filtro guarda. Mai `user.team`, che è uno solo per
            // persona e su due righe direbbe due volte la stessa cosa.
            squadre: squadreDellaTessera(ctx),
            // Il RUOLO della tessera, e nient'altro (F3c). Qui la spia
            // `team === "TEAMTD"` sostituiva il ruolo con se stessa, e la
            // riga di un allenatore con squadre finiva per dichiarare un
            // ruolo che non esiste. Le squadre le dice `squadre`, qui
            // sopra: due dati distinti, due campi distinti.
            displayRole: ctx.role,
          });
        });
      }
    });

    return result.filter((r) => {
      const fullName =
        `${r.user.firstName || ""} ${r.user.lastName || ""}`.toLowerCase();

      if (search && !fullName.includes(search.toLowerCase())) return false;

      if (roleFilter) {
        // ⚠️ Diceva «un TEAMTD è pur sempre un coach»: da F3c la spia non
        // entra più in `displayRole`, che porta il RUOLO della tessera. Il
        // filtro non cambia — guardava già anche il ruolo del contesto, ed
        // è per quella via che un coach con squadre rispondeva a COACH.
        //
        // ⚠️ `canonicalRole` e non `normalizza`: il confronto è fra RUOLI, e
        // le grafie storiche («Manager», «TD», «ManagerTD») sono allenatori
        // quanto un «COACH» (CLAUDE.md §2). Con la sola grafia, chiedere
        // COACH lasciava fuori proprio i tesserati non ancora migrati. La
        // canonicalizzazione vale sui DUE lati, filtro compreso: è una
        // lettura, non entra in nessun salvataggio.
        const ruoloRiga = canonicalRole(r.displayRole);
        const ruoloContesto = canonicalRole(
          (r.context && r.context.role) || r.user.role
        );
        const ruoloCercato = canonicalRole(roleFilter);
        if (ruoloRiga !== ruoloCercato && ruoloContesto !== ruoloCercato) {
          return false;
        }
      }

      // Filtro squadra: guarda le squadre DELLA TESSERA di questa riga.
      // «Unassigned» è la tessera senza squadre — e «tutte le squadre» non è
      // fra queste: quel permesso non è un elenco vuoto, è il suo contrario.
      // Un allenatore con «All teams» non risponde al filtro di UNA squadra,
      // esattamente come prima (il suo `team` valeva "TEAMTD" e non
      // corrispondeva a nessun nome).
      if (teamFilter === SENZA_SQUADRA) {
        if (r.squadre.allTeams || r.squadre.teams.length > 0) return false;
      } else if (teamFilter && !r.squadre.teams.includes(teamFilter)) {
        return false;
      }

      // ⚠️ FETTA A — l'UNICO dei quattro filtri che cambia asse: guardava lo
      // status della PERSONA (`r.user.status`), guarda ora quello della
      // TESSERA di questa riga. Search, Role e Team seguivano già le righe.
      // Una riga base (nessuna tessera) non ha uno status da confrontare:
      // esce da qualunque scelta di questo filtro, che è esattamente ciò che
      // dice — «mostrami le tessere in stato X».
      if (statusFilter && normalizza(r.context?.status) !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [coorteDiStagione, stagioneAttiva, search, roleFilter, teamFilter, statusFilter]);

  /* =======================
     BLOCCHI-PERSONA (doc 07 §8.1)
     Raggruppamento di SOLA RESA: le righe filtrate qui sopra si
     riuniscono per persona, nello stesso ordine in cui arrivano.
     Nessun filtro viene toccato: se un filtro nasconde un ruolo, il
     blocco mostra i soli ruoli superstiti (comportamento accettato in
     chat il 5/8/2026).
  ======================= */
  const blocchi = useMemo(() => {
    const ordine = [];
    const perPersona = new Map();

    rows.forEach((r) => {
      const id = r.user.id;
      if (!perPersona.has(id)) {
        perPersona.set(id, { id, user: r.user, righe: [] });
        ordine.push(id);
      }
      perPersona.get(id).righe.push(r);
    });

    return ordine.map((id) => perPersona.get(id));
  }, [rows]);

  /* =======================
     RENDER GUARDS
  ======================= */
  if (loading) {
    return (
      <HomeShell backTo="/home" width="admin">
        <h1 className="qpsc-page-title">Team Assignment</h1>
        <p className="qpsc-note ta-centered">Loading…</p>
      </HomeShell>
    );
  }

  if (error) {
    return (
      <HomeShell backTo="/home" width="admin">
        <h1 className="qpsc-page-title">Team Assignment</h1>
        <p className="ta-alert">{error}</p>
      </HomeShell>
    );
  }

  /* =======================
     RENDER
  ======================= */
  return (
    <HomeShell backTo="/home" width="admin">
      <h1 className="qpsc-page-title">Team Assignment</h1>
      <p className="qpsc-note ta-centered">
        Assign members to teams and manage their status
      </p>

      {/* LA BARRA DI STATO DELLA STAGIONE (doc 07 §8.2) — NUDA, senza alcuna
          prop: questa pagina la stagione la LEGGE, non la comanda.
          L'`override` serve a chi scrive la cassettina e subito dopo la
          conosce meglio del contesto (Season Management); la `note` parla di
          un gesto che qui non si fa.
          Dichiara la cornice dentro cui va letto tutto ciò che segue. NON è
          un filtro: il filtro è «Season Viewed», qui sotto fra i comandi.
          ⚠️ Sta come FIGLIA DIRETTA della colonna del telaio (`.qpsc-column`
          di HomeShell, `width="admin"`), che porta già `gap: 16px` fra i
          blocchi: lo spazio verticale non va scritto. E non va infilata
          dentro `.qpsc-admin-list`, `.qpsc-person`, `.qpsc-role` o le
          modali — sono griglie con regole `> *` in AdminTools.css, che la
          deformerebbero. */}
      <SeasonStatusBar />

      <div className="ta-toolbar">
        <div className="ta-toolbar__filters">
          {/* «SEASON VIEWED» — il PRIMO dei filtri, e non uno dei quattro:
              sceglie la COORTE su cui gli altri lavorano. Ricerca, ruolo,
              squadra e stato restringono un insieme; la stagione dice quale
              sia quell'insieme.
              ⚠️ Il nome è «Season Viewed» e non «Season»: a schermo convivono
              due cose che parlano di annate — la barra qui sopra dichiara come
              sta il CLUB, questa dice che cosa sto guardando IO. Chiamarle
              tutt'e due «Season» le farebbe leggere come la stessa.
              ⚠️ Il valore viaggia come STRINGA nel DOM e come NUMERO nello
              stato: la conversione sta qui, e in un punto solo.
              ⚠️ Il campo resta vuoto in un caso solo — nessun anno noto né dal
              club né dai dati — e in quel caso non c'è comunque nessuna voce
              da offrire. */}
          <div className="qpsc-field ta-toolbar__season">
            <span className="qpsc-field__label">Season Viewed</span>
            <select
              className="qpsc-select"
              aria-label="Season viewed"
              value={stagioneAttiva === null ? "" : String(stagioneAttiva)}
              onChange={(e) => setStagioneScelta(Number(e.target.value))}
            >
              {stagioniDisponibili.map((anno) => (
                <option key={anno} value={String(anno)}>{anno}</option>
              ))}
            </select>
          </div>

          <div className="qpsc-field">
            <span className="qpsc-field__label">Search</span>
            <input
              className="qpsc-input"
              aria-label="Search user"
              placeholder="Search user"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="qpsc-field">
            <span className="qpsc-field__label">Filter by role</span>
            <select
              className="qpsc-select"
              aria-label="Filter by role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All roles</option>
              {RUOLI.map((ruolo) => (
                <option key={ruolo} value={ruolo}>{ruolo}</option>
              ))}
            </select>
          </div>

          <div className="qpsc-field">
            <span className="qpsc-field__label">Filter by team</span>
            <select
              className="qpsc-select"
              aria-label="Filter by team"
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
            >
              <option value="">All teams</option>
              <option value={SENZA_SQUADRA}>— Unassigned —</option>
              {/* Le squadre PRESENTI NELL'ANNO GUARDATO (lavoro L), non
                  quelle del club: si filtra su ciò che la tabella può
                  davvero mostrare. «Unassigned» resta, con il suo criterio
                  invariato: è una condizione, non una squadra. */}
              {squadreInTabella.map((team) => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          </div>

          <div className="qpsc-field">
            <span className="qpsc-field__label">Filter by status</span>
            <select
              className="qpsc-select"
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              {STATI.map((stato) => (
                <option key={stato} value={stato}>{stato}</option>
              ))}
            </select>
          </div>

          {/* LA MODALITÀ FAMIGLIA — ULTIMA, E DI PROPOSITO NON UN FILTRO.
              ⚠️ Sta in fondo, dopo i quattro filtri, e si chiama «Show
              family links», non «Filter by family»: una spunta in mezzo a
              quattro tendine viene LETTA come un quinto filtro se non lo si
              dice. Questa non restringe niente — accende una riga in più
              sotto il nome.
              Forma di casa (`qpsc-check`), la stessa delle caselle delle
              squadre più sotto: nessun comando nuovo da imparare. */}
          <label className="qpsc-check ta-toolbar__family">
            <input
              className="qpsc-check__input"
              type="checkbox"
              checked={modalitaFamiglia}
              onChange={() => setModalitaFamiglia((attiva) => !attiva)}
            />
            <span className="qpsc-check__box" aria-hidden="true" />
            <span>Show family links</span>
          </label>
        </div>

        {isAdmin && (
          <button
            className="qpsc-tool-btn qpsc-tool-btn--wide ta-toolbar__action"
            onClick={() => setShowTeamManager(true)}
          >
            Manage Teams
          </button>
        )}
      </div>

      <div className="qpsc-admin-list">
        {/* Intestazioni di colonna: si vedono solo nella resa a tabella.
            SETTE colonne, nell'ordine della riga (doc 07 §8.1, rev.
            TA-VESTE): nome, Gender, ruolo, Team, Status, Approve, View
            Profile. Le ultime due non hanno titolo — sono comandi, non
            dati; «Gender» invece è scritta, perché porta un dato, ed è la
            seconda come nella pagina gemella (Admin User List). */}
        <div className="qpsc-admin-list__head" aria-hidden="true">
          <span className="qpsc-admin-list__col">Member</span>
          <span className="qpsc-admin-list__col">Gender</span>
          <span className="qpsc-admin-list__col">Role</span>
          <span className="qpsc-admin-list__col">Team</span>
          <span className="qpsc-admin-list__col">Status</span>
          <span className="qpsc-admin-list__col" />
          <span className="qpsc-admin-list__col" />
        </div>

        {blocchi.map((blocco) => {
          const u = blocco.user;

          // Lo stato della PERSONA — lo specchio dell'anno OPERATIVO. Da
          // questa fetta NON è più ciò che la tendina MOSTRA (v. poco sotto):
          // gli resta il solo mestiere di dire da dove PARTE il gesto, cioè
          // se una voce «ACTIVE» significhi «accendi le tessere dell'anno
          // guardato» oppure «riammetti un archiviato». Non governa né i
          // colori né i comandi delle righe: quelli vengono dalla tessera
          // dell'anno (lavoro H).
          const stato = normalizza(u.status);

          // LE TESSERE DELL'ANNO GUARDATO, lette UNA volta sola: le usano il
          // bottone Approve/Renew e la tendina Status, che devono raccontare
          // la stessa annata.
          // Si legge il dato della PERSONA, non le `blocco.righe`: le righe
          // sono filtrate anche da Status, e il bottone sparirebbe appena
          // l'amministratore filtra per ACTIVE, cioè proprio mentre cerca chi
          // resta da approvare. Lo stesso vale per la tendina, il cui gesto
          // agisce su TUTTE le tessere dell'anno, non sulle sole visibili.
          const tessereDellAnnoGuardato = tessereDelSoggetto(
            u?.contexts,
            stagioneAttiva,
            { stati: null }
          );

          // APPROVE / RENEW (lavoro J) — la comparsa la decidono le TESSERE.
          const tesserePendenti = tessereDellAnnoGuardato.filter(
            (c) => normalizza(c.status) === "PENDING"
          );
          const daApprovare = tesserePendenti.length > 0;

          // ⚠️ LO STATO CHE LA TENDINA MOSTRA È QUELLO DELL'ANNO GUARDATO
          // (fetta RENEW-01, intervento 3). Prima mostrava `u.status`, cioè lo
          // specchio dell'anno OPERATIVO: guardando il 2027, un rinnovato con
          // tessera 2027 PENDING appariva ACTIVE — il campo diceva il vero sul
          // suo anno, che però non era quello in pagina, e accanto stava un
          // bottone «Renew» che dichiarava il contrario. Ora la fonte è la
          // stessa del bottone e dei colori: le tessere di «Season Viewed».
          //
          // ⚠️ IL CAMPO `status` NON SI TOCCA: resta lo specchio dell'anno
          // operativo, e resta il metro del GESTO (`cambiaStatoDallaTendina`
          // riceve `stato`, non questo valore). Qui cambia solo ciò che si
          // MOSTRA.
          //
          // REGOLA DEL CASO RARO — la stessa del colore di fila, poco più
          // sotto, e per la stessa ragione: vince lo stato «più indietro»,
          // cioè «c'è ancora qualcosa da fare su questa persona».
          //   almeno una PENDING  → PENDING
          //   altrimenti tutte ARCHIVED → ARCHIVED
          //   altrimenti → ACTIVE
          //
          // ⚠️ NESSUNA TESSERA DELL'ANNO GUARDATO → SI RIPIEGA SULLA PERSONA.
          // Sono gli AMMINISTRATORI, che entrano in lista per eccezione e le
          // cui tessere non portano l'anno (stesso caso della «riga base», che
          // per il ruolo ripiega su `u.role`): dedurre PENDING dal vuoto li
          // mostrerebbe tutti in attesa d'approvazione. Vale anche con annata
          // ignota (`stagioneAttiva === null`), dove non c'è nessun anno da
          // raccontare.
          //
          // ⚠️ LA REGOLA VIVE IN `utils/squadre.js` (`statoDelleTessere`, cantiere
          // «User List per anno guardato»): la precedenza qui sopra è stata
          // portata lì INVARIATA, e User List la legge dalla stessa sede (via
          // `statoDelSoggetto`, che la compone con le tessere dell'anno). Qui
          // l'insieme in ingresso è `tessereDellAnnoGuardato`, letto UNA volta
          // sola poco sopra: lo stesso oggetto del bottone Approve/Renew, non
          // una seconda lettura delle tessere (rifinitura R4 — la dottrina
          // «non filtrare due volte» del modulo). Il ripiego resta `stato`,
          // esplicito.
          const statoMostrato = statoDelleTessere(tessereDellAnnoGuardato, stato);

          // L'ETICHETTA si biforca (decisione D-biforcazione, strada «a»):
          // «Renew» se la persona ha rinnovato PER l'annata guardata,
          // «Approve» altrimenti.
          // ⚠️ Nota onesta: `renewedSeason` arriva già normalizzato a intero
          // dal backend (`rinnovoDiLista` lo passa per `annoTollerante` dentro
          // `proiezioneDiLista`), quindi `annoDiLista` qui è una DIFESA di
          // grafia, non il rimedio a un difetto reale. Il precedente
          // `giaChiesto` di EnrolNextSeason.jsx vive su un'altra via di dato —
          // il documento grezzo del ramo «sé stessi» di `GET /users?uid=…` —
          // dove la tolleranza serve davvero.
          // ⚠️ La guardia su `stagioneAttiva === null` non è pignoleria: senza,
          // con l'annata ignota il confronto `null === null` direbbe «Renew»
          // per chiunque non abbia rinnovato mai.
          const haRinnovatoPerLAnno =
            stagioneAttiva !== null &&
            annoDiLista(u.renewedSeason) === stagioneAttiva;

          // La tendina mostra SEMPRE il valore vero. Le voci OFFERTE sono
          // però solo quelle selezionabili (`STATI_SELEZIONABILI`: PENDING
          // non è una destinazione). Lo stato corrente si aggiunge in testa
          // quando non è fra quelle — è il caso di una tessera PENDING, ed
          // è quello di un documento con uno stato fuori dai tre previsti:
          // così l'amministratore legge dove si trova e sceglie solo dove
          // può andare. Mai più una tendina vuota, mai più una voce che il
          // backend rifiuterebbe. (Restare sulla voce già selezionata non
          // fa scattare `onChange`: la presenza di PENDING in elenco è
          // lettura, non un'azione a portata di clic.)
          // ⚠️ Il valore da cui parte è ora `statoMostrato`, l'anno guardato:
          // il PENDING di un rinnovato entra in elenco come sola lettura,
          // esattamente come vi entrava quello di un nuovo iscritto.
          const opzioniStato = STATI_SELEZIONABILI.includes(statoMostrato)
            ? STATI_SELEZIONABILI
            : [statoMostrato || "", ...STATI_SELEZIONABILI].filter(
                (s, i, a) => a.indexOf(s) === i
              );

          // Ruolo singolo: in tabella la testata e l'unica riga di ruolo
          // stanno sulla STESSA riga (doc 07 §8.1), quindi il separatore
          // da 2px verso la persona successiva tocca anche alla testata.
          // Su card non cambia nulla: la classe non ha regole sotto i 768px.
          const ruoloSingolo = blocco.righe.length === 1;

          // ⚠️ COLORE DI FILA — D2-bis (cantiere TA-VESTE, 29/8/2026;
          // SOSTITUISCE la D2 della Fetta A, che qui teneva la testata
          // NEUTRA e lasciava l'ambra e il rosso alle sole RIGHE).
          // Il committente, visto il risultato in beta, vuole la fila
          // INTERA tinta: testata e righe insieme. Quindi la classe di
          // stato torna sulla <section> — ma NON è più lo `u.status` della
          // persona che la Fetta A aveva tolto: è calcolata qui sotto
          // dalle TESSERE DELL'ANNATA GUARDATA, con la regola del caso raro.
          // Da qui la tinta scende sulle righe (doc 07 §8.1, rev.
          // TA-VESTE): la sorgente del colore è UNA SOLA, la persona.
          // Le righe non raccontano più la propria tessera per il colore.
          //
          // REGOLA DEL CASO RARO (più tessere dello stesso anno in stati
          // diversi — raro ma reale): vince lo stato «più indietro», cioè
          // «c'è ancora qualcosa da fare su questa persona».
          //   almeno una PENDING → ambra
          //   altrimenti tutte ARCHIVED → rosso
          //   altrimenti → neutra
          // Così la fila non è mai mezza tinta e mezza no.
          //
          // ⚠️ Si guardano le RIGHE DEL BLOCCO, non `u.contexts` grezzo:
          // sono già filtrate all'annata, e sono esattamente ciò che si
          // vede. Con un filtro Status attivo la fila si colora di ciò che
          // resta a schermo, che è l'unica cosa che il lettore può
          // verificare. (È una scelta diversa da quella di `daApprovare`
          // qui sopra, che legge la persona di proposito: là il bottone
          // non deve sparire mentre l'admin filtra per ACTIVE.)
          // Una riga base (nessuna tessera dell'annata) porta stato vuoto:
          // non è ARCHIVED, quindi da sola tiene la fila neutra.
          //
          // ⚠️ LA PRECEDENZA È QUELLA DI `statoDelleTessere` (utils/squadre.js),
          // la stessa di `statoMostrato` qui sopra — e si passa alla versione
          // «su tessere già scelte» proprio perché l'insieme qui è quello
          // delle RIGHE, non della persona. Il ripiego "" è irraggiungibile
          // (un blocco nasce da almeno una riga) e, se mai lo fosse, vale
          // «né in attesa né archiviata»: la fila neutra di prima.
          const statoDellaFila = statoDelleTessere(
            blocco.righe.map((r) => r.context),
            ""
          );
          const filaInAttesa = statoDellaFila === "PENDING";
          const filaArchiviata = statoDellaFila === "ARCHIVED";

          // LA TERZA CONDIZIONE DI FILA: «ACTIVE, ma deve ancora completare»
          // (TA-INCOMPLETE). Il si'/no lo dice il backend — `mustCompleteSeason`,
          // che ricopia alla lettera il predicato del cancello del login — e qui
          // si legge e basta: nessun conto sulle tessere, che darebbe una
          // seconda verita' destinata a divergere da quella.
          //
          // ⚠️ MUTUAMENTE ESCLUSIVA CON LE ALTRE DUE, e l'ordine di precedenza
          // e' quello del lavoro: prima si APPROVA, poi si completa. Una fila
          // con una tessera PENDING dell'annata resta ambra col bottone, e non
          // cambia veste per questo flag; una fila tutta ARCHIVED resta rossa.
          // Cosi' la <section> porta al piu' una classe di stato, come prima.
          //
          // ⚠️ IL FLAG PARLA DELL'ANNO DI ENROLLMENT, non di «Season Viewed»:
          // dice cio' che l'app APPLICA a quella persona al login, che e'
          // l'unica cosa che l'amministratore possa poi chiederle. Per questo
          // l'evidenza si mostra SOLO mentre si guarda quell'anno
          // (`guardoAnnoIscrizione`, in testa al componente): su un'altra
          // annata il flag non parla, e marcare la riga direbbe il falso — il
          // rinnovante finiva ambra sul 2026 per una mancanza del 2027.
          const filaDaCompletare =
            guardoAnnoIscrizione &&
            !filaInAttesa && !filaArchiviata && u.mustCompleteSeason === true;

          // `qpsc-person--single` resta e non c'entra con lo stato: è
          // geometria — con un ruolo solo, testata e riga stanno sulla
          // stessa fila.
          return (
            <section
              key={blocco.id}
              className={
                "qpsc-person" +
                (ruoloSingolo ? " qpsc-person--single" : "") +
                (filaInAttesa ? " qpsc-person--pending" : "") +
                (filaArchiviata ? " qpsc-person--archived" : "") +
                (filaDaCompletare ? " qpsc-person--incomplete" : "")
              }
            >
              {/* I due gusci ident/actions servono alla sola resa a tabella:
                  sotto i 768px sono `display: contents`, quindi nome, nota,
                  pastiglia e comandi restano figli diretti della testata.
                  (rev. TA-MOBILE) Su card la testata è una GRIGLIA: nome,
                  pastiglia e nota sulla prima riga — è così che la pastiglia
                  non va più a capo con un nome lungo — e sotto, a piena
                  larghezza, Status, Approve e View Profile. */}
              <header className="qpsc-person__head">
                <div className="qpsc-person__ident">
                  {/* Il guscio del nome resta anche ora che la pastiglia è
                      uscita di qui: è la cella del nome — la prima colonna,
                      a entrambe le larghezze — e tiene il nome insieme a
                      qualunque cosa un domani gli si rimetta dentro, senza
                      mandarla a capo (doc 07 §8.1, rev. collaudo 5/8/2026).
                      (rev. TA-MOBILE) Che la pastiglia resti sulla riga del
                      nome, su card, non dipende più da questo guscio ma
                      dalle colonne della testata a griglia. */}
                  <span className="qpsc-person__title">
                    <span className="qpsc-person__name">
                      {u.firstName} {u.lastName}
                    </span>
                  </span>
                  {blocco.righe.length > 1 && (
                    <span className="qpsc-person__meta">
                      {blocco.righe.length} roles
                    </span>
                  )}

                  {/* LA RIGA FAMIGLIA (cantiere FAMIGLIA-LISTE, 9/9/2026).
                      Sta DENTRO `__ident`, insieme al nome, e non come figlia
                      diretta della testata: sopra i 768px la testata è
                      `display: contents` e le sue figlie diventano colonne
                      della tabella — una riga appesa lì sfaserebbe tutte le
                      altre (è la stessa trappola già scritta per la cella
                      Gender qui sotto). Dentro `__ident`, che lì è una
                      scatola flex vera, va a capo sotto il nome e resta nella
                      prima colonna; sotto i 768px `__ident` sparisce dal
                      flusso e la riga diventa una cella della griglia della
                      testata, a piena larghezza. Le due misure stanno nel
                      foglio, non qui.
                      ⚠️ Nessun comando: è testo. Il collegato si apre dalla
                      SUA riga, che c'è per costruzione — la variante «b» non
                      nomina chi la riga non ce l'ha. */}
                  {modalitaFamiglia &&
                    famigliaDiPersona(u).map((riga) => (
                      <span key={riga.chiave} className="qpsc-person__family">
                        {riga.testo}
                      </span>
                    ))}
                </div>

                {/* COLONNA «GENDER» (cantiere TA-VESTE, doc 07 §8.1 rev.
                    TA-VESTE). La pastiglia categoria era accanto al nome,
                    dentro `qpsc-person__title`; ora ha una CELLA propria,
                    seconda colonna, come nella pagina gemella (Admin User
                    List). Componente e chiamata sono gli stessi di prima:
                    cambia solo dove la pastiglia è appesa.
                    ⚠️ La cella è figlia diretta della testata, non del
                    guscio `__ident`: sopra i 768px `__ident` è una scatola
                    vera che occupa la colonna 1, quindi una pastiglia messa
                    lì dentro non avrebbe mai una colonna sua.
                    UNA VOLTA PER PERSONA: la categoria è dato della
                    persona, non del ruolo, quindi sta solo sulla testata —
                    sulle righe di ruolo successive quella colonna resta
                    vuota (la copre lo spacer, esteso a 1/3 nel foglio).
                    LA PASTIGLIA DICE LA CATEGORIA DELLA PERSONA (Fetta A,
                    decisione D3): genere + età compiuta al 31 dicembre
                    dell'ANNATA GUARDATA, dalla sede unica
                    `categoriaAnagrafica`; `gender` e `dob` sono già nella
                    proiezione di `GET /users`, nessuna chiamata nuova.
                    Chi non ha genere o data di nascita leggibili la perde:
                    `categoriaAnagrafica` tace di proposito, perché un
                    timbro inventato sarebbe peggio del silenzio — e la
                    cella resta vuota, tenendo la colonna. */}
                <div className="qpsc-person__gender">
                  <PastigliaCategoriaSeNota
                    category={categoriaAnagrafica(u, stagioneAttiva)}
                  />
                </div>

                {/* STATO — UNA VOLTA PER PERSONA (F3b, D3).
                    La TENDINA è un gesto della PERSONA (rotta
                    PUT /users/:id/status): mostrarla su ogni riga di ruolo
                    la faceva sembrare un dato della tessera, e ripeteva lo
                    stesso comando due volte, con lo stesso effetto.

                    ⚠️ SECONDA RETTIFICA (fetta RENEW-01, intervento 3): ciò
                    che la tendina MOSTRA non è più `user.status` ma lo stato
                    delle tessere dell'ANNO GUARDATO (`statoMostrato`, calcolato
                    in testa al blocco). Il campo `user.status` resta dov'era —
                    è lo specchio dell'anno operativo, e lo leggono ~25 punti
                    dell'app — e resta il metro del GESTO. Ciò che si mostra e
                    ciò che si fa guardano ora la stessa annata: il gesto
                    agiva già sull'anno guardato (Fetta B).

                    ⚠️ RETTIFICA (Fetta A). Qui c'era scritto che «le tessere
                    hanno uno status proprio, che qui NON si mostra e non si
                    tocca». La prima metà non è più vera e la seconda sì, ed è
                    una scelta, non una dimenticanza:
                      - lo status della TESSERA ORA SI MOSTRA — è lui a
                        governare la resa della riga di ruolo (colore ambra
                        PENDING / rosso ARCHIVED, comandi spenti, comparsa di
                        Approve/Renew): il dato d'annata sta sulla tessera, e
                        la riga che lo mostra è la sua;
                      - lo status della tessera NON SI SCRIVE da qui, perché
                        una scrittura per-tessera dello status oggi NON ESISTE
                        nel backend. È lavoro della Fetta B. Finché non c'è,
                        questa tendina resta ciò che è sempre stata: il gesto
                        della persona, con l'archiviazione-famiglia e le sue
                        conferme.
                    Colori dalla tessera, gesto sulla persona: due cose
                    diverse, dette in due posti diversi.

                    L'etichetta sta SOPRA la tendina, non accanto (doc 07
                    §8.1, rev. F10): il guscio è quello dei campi §8, e
                    sopra i 768px l'etichetta sparisce perché la dice
                    l'intestazione di colonna.

                    ⚠️ P2-bis: il guscio è FIGLIO DELLA TESTATA, non più di
                    `.qpsc-person__actions`. La testata è `display: contents`
                    in tabella, quindi ogni suo figlio è una cella della
                    griglia e può stare nella colonna che gli spetta: questa
                    è la 5, sotto il titolo «Status». Prima stava in coda
                    con Approve e View Profile, e la colonna dello stato
                    restava vuota sotto il proprio titolo. Solo posizione nel markup: la
                    tendina, il suo valore, il suo onChange, il suo disabled
                    e le sue opzioni sono gli stessi di prima. */}
                <div className="qpsc-field qpsc-person__status">
                  <span className="qpsc-field__label">Status</span>
                  <select
                    // §8.1: il valore mostrato porta il colore del
                    // proprio stato (ambra, verde, rosso).
                    className={"qpsc-select" + classeStato(statoMostrato)}
                    aria-label="Status"
                    value={statoMostrato || ""}
                    // ⚠️ Lo STATO CORRENTE viaggia col cambio: sotto la voce
                    // ACTIVE vivono due gesti diversi — «accendi le tessere
                    // dell'anno guardato» e «riammetti un archiviato» — e a
                    // distinguerli serve da dove si parte.
                    // ⚠️ E QUELLO CHE VIAGGIA È `stato`, LA PERSONA, non
                    // `statoMostrato`: il rientro avviene nell'anno OPERATIVO,
                    // ed è lo specchio a dire se questa persona sia davvero
                    // un'archiviata da riammettere (l'avvertenza per esteso è
                    // su `cambiaStatoDallaTendina`). Chi è ACTIVE oggi e ha le
                    // tessere 2025 archiviate si vede ARCHIVED guardando il
                    // 2025 — ed è vero — ma non è un rientro.
                    onChange={(e) =>
                      cambiaStatoDallaTendina(u.id, e.target.value, stato)
                    }
                    disabled={!isAdmin}
                  >
                    {/* ⚠️ ANNO IGNOTO → LA VOCE «ARCHIVED» È SPENTA.
                        Archiviare spegne le tessere dell'ANNATA GUARDATA, e
                        con `stagioneAttiva === null` quell'annata non c'è:
                        il gesto morirebbe subito dopo, sulla guardia di
                        `archiviaFamiglia` («Select a season before
                        archiving»). Meglio non offrirlo affatto che farlo
                        partire e fermarlo. La guardia nel gesto RESTA — è
                        la rete di sicurezza, questa è solo la resa. */}
                    {opzioniStato.map((s) => (
                      <option
                        key={s || "empty"}
                        value={s}
                        disabled={s === "ARCHIVED" && stagioneAttiva === null}
                      >
                        {s || "—"}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Promozione ESPLICITA: l'unico gesto che porta
                    PENDING → ACTIVE. Nessuna promozione automatica.
                    P2-bis: ha una cella sua, la colonna 6, accanto alla
                    tendina di stato e prima di View Profile. La cella c'è
                    sempre — anche quando il pulsante non c'è — perché è lei
                    a tenere la colonna e a far correre il filetto di riga;
                    vuota, non occupa larghezza.

                    ⚠️ FETTA A — LA COMPARSA VIENE DALLE TESSERE, IL BOTTONE
                    RESTA IN TESTATA. Prima rispondeva a `u.status ===
                    "PENDING"`; ora compare quando la persona ha almeno una
                    tessera PENDING dell'anno guardato. Resta però UNO PER
                    PERSONA, e non scende sulla riga: il gesto sottostante
                    (PUT /users/:id/status) attiva INSIEME tutte le tessere
                    pendenti dell'anno operativo, quindi un bottone
                    per-persona è l'immagine fedele di ciò che il backend fa.
                    Un bottone per-riga prometterebbe «approvo QUESTA tessera»
                    mentre il backend ne tocca più d'una — e aprirebbe la
                    geometria delle colonne, che resta a sette.

                    ⚠️ La testata «neutra» (D2) riguarda il COLORE di stato,
                    non i comandi: colori sulle righe, comandi di persona in
                    testata.

                    ⚠️ IL GESTO AGISCE SULL'ANNO GUARDATO (Fetta B). Non passa
                    più da `changeStatus`, che mandava il solo `{ status }` e
                    lasciava scegliere le tessere al backend con l'anno
                    OPERATIVO: guardando un'altra annata la persona diventava
                    ACTIVE e la tessera mostrata restava PENDING. Ora
                    `attivaTessereDellAnno` porta con sé `stagioneAttiva`, e il
                    backend accende le tessere PENDING di QUELL'anno.
                    L'etichetta resta un ternario di sola veste: Approve e
                    Renew sono lo stesso gesto, nessun ramo li distingue. */}
                <div className="qpsc-person__approve">
                  {daApprovare && isAdmin && (
                    <button
                      className="qpsc-tool-btn qpsc-tool-btn--row"
                      onClick={() => attivaTessereDellAnno(u.id)}
                    >
                      {haRinnovatoPerLAnno ? "Renew" : "Approve"}
                    </button>
                  )}

                  {/* «INCOMPLETE» — una LABEL, non un comando, e sta qui perche'
                      questa e' la colonna che dice «cosa manca su questa
                      persona». Non ha `onClick` e non deve averlo: completare i
                      dati e' gesto del SOCIO, dalla sua finestra di
                      completamento, e non esiste porta con cui un
                      amministratore lo faccia al posto suo.

                      ⚠️ MUTUAMENTE ESCLUSIVA COL BOTTONE, e la guardia e'
                      `!daApprovare` — la stessa condizione del bottone,
                      negata — non `filaDaCompletare`. Non e' una svista:
                      `daApprovare` legge le TESSERE DELLA PERSONA, mentre
                      `filaInAttesa` (dentro `filaDaCompletare`) legge le righe
                      VISIBILI, gia' passate dal filtro Status. Con un filtro
                      attivo che nasconde le PENDING le due divergono, e in
                      questa cella deve vincere quella del bottone: altrimenti
                      bottone e label finirebbero uno accanto all'altra. */}
                  {guardoAnnoIscrizione && !daApprovare && u.mustCompleteSeason === true && (
                    <span className="qpsc-person__await-label">INCOMPLETE</span>
                  )}
                </div>

                <div className="qpsc-person__actions">
                  {/* §5.4: View Profile ha una forma sua — oro pieno senza
                      bordo rosso — e non è più un fantasma §5.3. */}
                  <button
                    className="qpsc-profile-btn qpsc-profile-btn--wide"
                    onClick={() => navigate(`/profile/${u.id}`)}
                  >
                    View Profile
                  </button>
                </div>
              </header>

              {blocco.righe.map((r, i) => {
                // LA TESSERA DI QUESTA RIGA. Tutto ciò che segue — cosa si
                // mostra e su cosa si scrive — viene da lei e da nessun
                // campo della persona.
                const tessera = r.context;
                const idTessera = tessera?.id || "";
                const squadre = r.squadre;

                // LO STATO DELLA TESSERA DI QUESTA RIGA (lavoro H della
                // Fetta A). Governa ancora ciò che si può FARE sulla riga
                // (`puoiAssegnare`, più sotto): le squadre si assegnano solo
                // su una tessera ACTIVE, anche se un'altra tessera della
                // stessa persona è in un altro stato.
                // ⚠️ NON governa più il COLORE (D2-bis, cantiere TA-VESTE):
                // la tinta della fila scende tutta dalla persona — vedi la
                // nota sopra la <section> — e le classi di riga
                // `qpsc-role--pending` / `qpsc-role--archived` sono state
                // TOLTE da qui. Tenerle avrebbe significato due sorgenti di
                // tinta sulle stesse celle: innocuo per il colore (è lo
                // stesso), rovinoso per l'opacità dell'archiviato, che si
                // sarebbe composta due volte (0,5 × 0,5 = 0,25 invece del
                // 50% che il §5 chiede).
                const statoTessera = normalizza(tessera?.status);

                // Allenatore per RUOLO, non per la spia `team === "TEAMTD"`.
                // La spia dice come sono scritti i vecchi campi, non che
                // cosa è la tessera: un coach senza squadre non la porta
                // (`team: ""`) e si sarebbe visto offrire il selettore di
                // UNA squadra. `canonicalRole` perché un coach scritto
                // «Manager» o «TD» è un coach (CLAUDE.md §2).
                const eAllenatore = canonicalRole(tessera?.role) === "COACH";

                // RUOLI SENZA SQUADRE: Parent, Admin e Super Admin. Non «non
                // gliene sono state assegnate», ma «quel ruolo non ne prende»
                // — le squadre di una famiglia sono quelle dei FIGLI, e
                // stanno sulle tessere PLAYER dei figli; un amministratore
                // non ha squadre in nessun senso. Offrire qui un selettore
                // significava invitare a scrivere un dato che nessun lettore
                // usa, e lasciarne uno spento significava dire «qui si
                // potrebbe, ma non ora», che è falso (doc 07 §8.1, rev. F10).
                //
                // ⚠️ SI GUARDA IL RUOLO DELLA RIGA, non quello della persona:
                // è `r.displayRole`, la stessa identica informazione con cui
                // la riga stampa la propria etichetta poche righe più sotto
                // (`etichettaRuolo(r.displayRole)`), e vale sia per le righe
                // nate da una tessera (`ctx.role`) sia per la riga base di
                // chi tessere non ne ha (`u.role`). Nessuna lettura nuova,
                // nessun dato in più: la riga sapeva già dirlo.
                // È il punto della fetta: una persona Player+Parent ha due
                // righe, e mostra le squadre sulla riga Player e NIENTE su
                // quella Parent.
                const ruoloDellaRiga = canonicalRole(r.displayRole);
                const senzaSquadrePerRuolo =
                  ruoloDellaRiga === "PARENT" ||
                  ruoloDellaRiga === "ADMIN" ||
                  ruoloDellaRiga === "SUPERADMIN";

                // RIGA BASE: la persona non ha NESSUNA tessera attiva, quindi
                // questa riga non è nata da una tessera (`context: null`) e
                // non ha un bersaglio su cui scrivere. Al posto di un comando
                // inerte si scrive perché non c'è nulla da comandare: la
                // prima tessera si crea in Manage Contexts (doc 07 §8.1,
                // rev. F10). Diverso dalla tessera STORICA senza codicino,
                // che una tessera ce l'ha: quella resta un comando spento
                // (50%, §5 «Stati»), perché il codicino le nascerà al primo
                // salvataggio da Manage Contexts.
                const senzaTessera = !tessera;

                // Si scrive SOLO su una tessera che ha un nome proprio: la
                // porta per-tessera si chiama per codicino, e una tessera
                // storica che non ce l'ha (o una riga senza tessera) non è
                // un bersaglio. Il codicino nasce al primo salvataggio da
                // Manage Contexts. Resta il gate di sempre: sola lettura per
                // i non-admin.
                //
                // ⚠️ FETTA A: la tessera che conta è quella DI QUESTA RIGA,
                // non la persona. Una persona può avere una tessera
                // archiviata e una viva nello stesso anno: guardare lo stato
                // della persona toglieva il comando dove il comando ha senso.
                //
                // ⚠️ CODA 3: si assegna solo su tessera ACTIVE. Prima si
                // approva, poi si assegna — il gesto non ha senso su chi non
                // è ancora approvato. Non è una regola nuova: il backend
                // rifiuta già le squadre su una tessera non-ACTIVE
                // (`server.js`, 400 «Cannot set teams on a non-active
                // context»); qui il frontend smette di offrire un gesto che
                // la porta rifiuta. PENDING e ARCHIVED cadono entrambi qui,
                // e il bottone NON si rende affatto (vedi la cella Team più
                // sotto): un comando spento direbbe «qui si potrebbe, ma non
                // ora», e su una tessera non approvata non si potrà finché
                // non la si approva.
                // ⚠️ ANNO IGNOTO → IL BOTTONE NON C'È. Da questa porta si
                // scrive sull'annata GUARDATA, e con `stagioneAttiva === null`
                // quell'annata non esiste: il Save partirebbe senza `season` e
                // morirebbe sul 400 del backend con la pagina d'aspetto
                // perfettamente normale. Meglio non offrire il gesto che
                // farlo partire e fermarlo. È la stessa dottrina con cui la
                // voce «ARCHIVED» si spegne nella tendina Status, e
                // la stessa forma con cui questo bottone già sparisce su una
                // tessera non-ACTIVE (Coda 3, qui sopra): un comando spento
                // direbbe «qui si potrebbe, ma non ora»; qui la strada per
                // potere non è aspettare, è scegliere un'annata.
                // Il 400 del server RESTA, ed è la rete: nell'uso normale
                // l'amministratore non lo incontra.
                const puoiAssegnare =
                  isAdmin &&
                  Boolean(idTessera) &&
                  statoTessera === "ACTIVE" &&
                  stagioneAttiva !== null;

                // Quante squadre ha già approvate: dato già in pagina,
                // letto dalla tessera della riga, nessuna chiamata nuova.
                //
                // Si contano le sole voci che corrispondono a una squadra
                // ESISTENTE (riparazione 5/8/2026, T3-G-sexies): l'elenco
                // può nominare una squadra cancellata da allora, e la modale
                // Approved Teams — che disegna una casella per ogni squadra
                // esistente — quelle voci non le mostra. Contandole, il
                // numero in riga e le spunte della finestra direbbero cose
                // diverse sullo stesso dato. Il dato NON si tocca: cambia
                // solo ciò che si conta.
                //
                // ⚠️ Da F3b si conta `teams`, non `approvedTeams`: la
                // sentinella non entra più nell'elenco (sta in `allTeams`),
                // quindi il filtro non deve più difendersi da lei.
                const approvate = squadre.teams.filter((nome) =>
                  insiemeTeam.has(nome)
                ).length;

                // Con «tutte le squadre» non si conta niente: il permesso non
                // è un elenco di squadre, è «tutte». Un numero — qualunque
                // numero — direbbe il falso.
                // Nome diverso dallo stato `tutteLeSquadre` della modale
                // di proposito: sono due cose distinte — questo è il dato
                // della RIGA, quello è la spunta della FINESTRA — e due
                // cose distinte non portano lo stesso nome.
                const haTutteLeSquadre = squadre.allTeams;

                return (
                  <div key={r.key} className="qpsc-role">
                    {/* Ruoli successivi al primo: la cella del nome resta
                        vuota e porta il rientro di 32px con il filetto
                        verticale (doc 07 §8.1). Serve solo alla tabella:
                        sotto i 768px è `display: none`. */}
                    {i > 0 && (
                      <span className="qpsc-role__spacer" aria-hidden="true" />
                    )}

                    <div className="qpsc-role__label">
                      <span className="qpsc-role__name">
                        {etichettaRuolo(r.displayRole)}
                      </span>
                      {/* Nessuna pastiglia di stato in riga (doc 07 §8.1,
                          rev. collaudo 5/8/2026): lo stato lo dicono il
                          colore della tendina Status, l'evidenza della riga
                          e il pulsante Approve. La scritta ripetuta è
                          abolita. Resta la sola pastiglia CATEGORIA (§2),
                          che sta nella testata, accanto al nome. */}
                      {/* ⚠️ FETTA A, lavoro K: la nota dice ora il dato di
                          squadra di OGNI ruolo che ne ha uno, non del solo
                          allenatore. Serve perché la cella Team è diventata
                          un COMANDO anche per il giocatore (una sola modale
                          per tutti): senza questa nota, la squadra assegnata
                          non si leggerebbe più da nessuna parte — prima la
                          diceva la tendina. Un allenatore conta le squadre
                          approvate («All teams» o il numero), un giocatore ne
                          ha una sola e la nota la nomina. */}
                      {eAllenatore
                        ? (haTutteLeSquadre || approvate > 0) && (
                            <span className="qpsc-role__note">
                              {haTutteLeSquadre
                                ? "All teams"
                                : `${approvate} approved ${approvate === 1 ? "team" : "teams"}`}
                            </span>
                          )
                        : !senzaSquadrePerRuolo &&
                          squadre.teams[0] && (
                            <span className="qpsc-role__note">
                              {squadre.teams[0]}
                            </span>
                          )}
                    </div>

                    <div className="qpsc-role__cell qpsc-role__cell--team">
                      {/* PARENT, ADMIN, SUPER ADMIN: NIENTE, non
                          «disabilitato». Un comando spento dice «qui si
                          potrebbe, ma non ora»; qui non si potrà mai, e la
                          cella resta vuota. Il contenitore invece resta —
                          come la cella Status accanto — perché è lui a tenere
                          la riga allineata alle colonne della resa a tabella.
                          Subito dopo viene la riga senza tessera, che una
                          cella vuota non basta a spiegare: lì si scrive
                          dov'è la porta. Solo resa: nessun dato, nessun
                          permesso, nessun payload cambia. */}
                      {/* ⚠️ FETTA A, lavoro K — UNA SOLA MODALE PER TUTTI I
                          RUOLI CON SQUADRE. Qui c'erano due rese diverse
                          dello stesso gesto: il bottone «Approved Teams» per
                          l'allenatore e una tendina in linea per il
                          giocatore. La SCRITTURA era già la stessa
                          (`salvaSquadreTessera` → la porta per-tessera, stesso
                          payload `{ teams, allTeams }`): era diversa solo la
                          resa, e due rese dello stesso gesto si spiegano due
                          volte e divergono una volta.
                          La modale è ora unica e si adatta al ruolo della
                          TESSERA: N spunte più «All teams» per l'allenatore,
                          scelta singola e nessuna «All teams» per il
                          giocatore — su un non-coach quel permesso si
                          perderebbe in silenzio. Il vincolo «una squadra
                          sola» resta nel frontend, com'era.
                          La squadra assegnata si legge nella nota accanto al
                          ruolo, qui sopra. */}
                      {senzaSquadrePerRuolo ? null : senzaTessera ? (
                        <span className="qpsc-role__none">No context</span>
                      ) : (
                        /* ⚠️ CODA 3 — IL BOTTONE C'È SOLO SE SI PUÒ DAVVERO
                           ASSEGNARE, cioè su una tessera ACTIVE
                           (`puoiAssegnare`, sopra). Su PENDING e su ARCHIVED
                           non si rende affatto e la cella resta VUOTA, senza
                           testo sostitutivo: non un comando grigio — un
                           comando spento dice «qui si potrebbe, ma non ora»,
                           e la strada per potere non è aspettare, è
                           approvare la tessera dalla tendina Status in
                           testata. Cambia anche la resa degli ARCHIVED, che
                           prima mostravano il bottone grigio: deciso così.
                           Si condiziona IL BOTTONE, non la cella: il
                           contenitore `--team` resta sempre, è lui a tenere
                           la colonna allineata.
                           Per un non-admin la pagina è di sola lettura senza
                           eccezioni (e il backend rifiuta comunque). */
                        puoiAssegnare && (
                          <button
                            className="qpsc-ghost-btn qpsc-ghost-btn--wide"
                            // La finestra si apre SULLA TESSERA di questa
                            // riga: è il suo codicino che partirà al Save.
                            onClick={() => openTeamTDModal(u, tessera)}
                          >
                            {eAllenatore ? "Approved Teams" : "Assign Team"}
                          </button>
                        )
                      )}
                    </div>

                    {/* Le colonne dei comandi della PERSONA — stato (5),
                        Approve (6), View Profile (7) — sono già occupate
                        dalla testata, che in tabella sta sulla stessa riga
                        del primo ruolo. Sulle righe di ruolo successive
                        restano invece scoperte: una cella sola le copre
                        tutte e tre, così il filetto di riga non si
                        interrompe. Non contiene nulla e non deve contenere
                        nulla: quei tre comandi valgono per la persona e
                        stanno una volta sola.
                        Prima ce n'erano due, sempre presenti e sempre
                        vuote, perché lo stato stava in coda con gli altri:
                        P2-bis le sostituisce con questa, che c'è solo dove
                        serve. */}
                    {i > 0 && (
                      <div
                        className="qpsc-role__cell qpsc-role__cell--rest"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>

      {/* TEAMTD APPROVED TEAMS MODAL
          La finestra lavora su UNA tessera (`teamTDTessera`): si apre solo
          se la riga ne ha portata una, così il Save ha sempre un codicino a
          cui parlare. */}
      {teamTDUser && teamTDTessera && (
        <div className="qpsc-modal-overlay">
          {/* `ta-teams-modal` è la classe che porta la resa desktop di QUESTA
              sola finestra (TeamAssignment.css, blocco ≥768px). Senza di
              essa le stesse regole raggiungerebbero il guscio condiviso e
              quindi anche Readmit qui sotto e ogni altra modale dell'app. */}
          <div className="qpsc-modal ta-teams-modal">
            {/* Il titolo dice di che cosa si sta parlando: un allenatore ha
                squadre APPROVATE (quante ne servono), un giocatore ha LA sua
                squadra, una sola. Stessa finestra, stessa scrittura. */}
            <h2 className="qpsc-modal__title">
              {tesseraDelCoach ? "Approved Teams" : "Team"} —{" "}
              {teamTDUser.firstName} {teamTDUser.lastName}
            </h2>

            {/* RICERCA — sta FUORI dal corpo, fra il titolo e l'elenco: il
                corpo è l'unica parte che scorre (§8), quindi da qui il campo
                resta fermo mentre le squadre scorrono sotto.
                Forma di casa: la stessa `.qpsc-field` + `.qpsc-input` della
                barra dei filtri di pagina, nessuno stile inventato.
                ⚠️ Si vede SOLO da 768px in su: sotto quel confine il foglio
                lo nasconde e `teamSearchQuery` resta "", cioè il mobile
                mostra l'elenco intero esattamente come prima. */}
            <div className="qpsc-field ta-teams-search">
              <span className="qpsc-field__label">Search</span>
              <input
                className="qpsc-input"
                aria-label="Search teams"
                type="text"
                placeholder="Search teams…"
                value={teamSearchQuery}
                onChange={(e) => setTeamSearchQuery(e.target.value)}
              />
            </div>

            <div className="qpsc-modal__body">
              {/* §8.1 (rev. 6/8/2026): la sentinella ha una spunta sua, in
                  testa all'elenco. Attiva, dice che il permesso è «tutte
                  le squadre» — non un elenco — e le squadre sotto si
                  mostrano spuntate e spente, perché sceglierle non
                  aggiungerebbe nulla. Il filetto sotto la separa
                  dall'elenco: senza, si leggerebbe come una squadra di
                  nome «All teams».
                  ⚠️ FETTA A: la spunta è SOLO dell'allenatore. Su una tessera
                  non-coach il permesso «tutte le squadre» si perde in
                  silenzio (nessun lettore lo onora), e offrirlo prometterebbe
                  un effetto che non c'è. Non si mostra spento: non si potrà
                  mai, e un comando spento direbbe «qui si potrebbe, ma non
                  ora». */}
              {tesseraDelCoach && (
                <label
                  className={`qpsc-check ta-check-all${!isAdmin ? " qpsc-check--disabled" : ""}`}
                >
                  <input
                    className="qpsc-check__input"
                    type="checkbox"
                    checked={tutteLeSquadre}
                    onChange={() => setTutteLeSquadre((attiva) => !attiva)}
                    disabled={!isAdmin}
                  />
                  <span className="qpsc-check__box" aria-hidden="true" />
                  <span>All teams</span>
                </label>
              )}

              {/* ⚠️ FILTRO DI SOLA VISTA (cantiere ASSIGNTEAM-DESKTOP).
                  Si filtra QUI, nel punto in cui si rende — non a monte, e
                  mai sullo stato: `selectedTeams` e `tutteLeSquadre` non
                  sanno che questo campo esiste. Una squadra spuntata e poi
                  filtrata via resta spuntata e parte al Save; una che rientra
                  nel filtro si ripresenta con la spunta giusta, perché la
                  spunta la legge sempre `selectedTeams`.
                  Confronto senza maiuscole e su sotto-stringa; il testo si
                  ripulisce ai bordi, così uno spazio di troppo non svuota
                  l'elenco. Con il campo vuoto `includes("")` è sempre vero:
                  nessun filtro, elenco intero — ed è il caso del mobile,
                  dove il campo non si vede nemmeno.
                  «All teams» NON passa di qui: è un comando, non una
                  squadra, e resta sempre a vista. */}
              {teamNames
                .filter((team) =>
                  team
                    .toLowerCase()
                    .includes(teamSearchQuery.trim().toLowerCase())
                )
                .map((team) => (
                  <label
                    key={team}
                    className={`qpsc-check${
                      !isAdmin || tutteLeSquadre ? " qpsc-check--disabled" : ""
                    }`}
                  >
                    {/* Il comando resta questo input, con lo stesso stato e lo
                        stesso onChange di prima: il quadrato §8 lo disegna lo
                        <span> accanto, fuori dalla portata delle regole sui tag
                        nudi che lo deformavano (vedi AdminTools.css §4). */}
                    {/* Con «All teams» attiva la casella si mostra spuntata
                        e spenta: è la resa del permesso pieno, non una
                        scelta dell'admin — e infatti non entra nell'array
                        che parte al Save.
                        ⚠️ FETTA A: su una tessera NON allenatore queste caselle
                        valgono a SCELTA SINGOLA — spuntarne una spegne la
                        precedente (`toggleApprovedTeam`), perché un giocatore
                        ha una squadra sola. Il vincolo resta nel frontend,
                        com'era prima nella tendina: il backend non lo impone. */}
                    <input
                      className="qpsc-check__input"
                      type="checkbox"
                      checked={tutteLeSquadre || selectedTeams.includes(team)}
                      onChange={() => toggleApprovedTeam(team)}
                      disabled={!isAdmin || tutteLeSquadre}
                    />
                    <span className="qpsc-check__box" aria-hidden="true" />
                    <span>{team}</span>
                  </label>
                ))}
            </div>

            <div className="qpsc-modal__actions">
              {/* Scrittura riservata agli admin, come Approve. */}
              {isAdmin && (
                <button className="qpsc-tool-btn" onClick={saveApprovedTeams}>
                  Save
                </button>
              )}
              <button className="qpsc-ghost-btn" onClick={chiudiTeamTDModal}>
                {isAdmin ? "Cancel" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* READMIT MODAL — la scelta dei figli che rientrano (Coda 4, R4).
          Stessa veste della finestra qui sopra: overlay + `qpsc-modal`, titolo,
          corpo, azioni, e le caselle nella forma di casa (`qpsc-check`). Non
          serve una classe nuova: il pattern era già qui.
          La sola presenza di `rientroModale` la fa comparire, ed è ciò che
          rende superflua ogni attesa: `riammettiFamiglia` setta e finisce. */}
      {rientroModale && (
        <div className="qpsc-modal-overlay">
          <div className="qpsc-modal">
            <h2 className="qpsc-modal__title">
              Readmit — {rientroModale.nome || "member"}
            </h2>

            <div className="qpsc-modal__body">
              {/* L'ANNO SI DICE, e si dice PRIMA della scelta: il rientro
                  avviene nell'anno OPERATIVO, che spesso NON è quello che
                  l'amministratore sta guardando. Chi sceglie deve sapere in
                  quale stagione sta rimettendo un bambino. */}
              <p className="qpsc-modal__note">
                {rientroModale.nome || "This member"} will return as PENDING for
                the {rientroModale.operatingSeason} season
                {frasePerRuoli(rientroModale.target?.roles)}.
              </p>

              <p className="qpsc-modal__note">
                Select which child profiles return with them:
              </p>

              {/* ⚠️ NESSUNA SPUNTA DI DEFAULT: nessuna cascata cieca. Ogni
                  figlio si sceglie uno per uno, e chi non si spunta resta dov'è.
                  ⚠️ I FIGLI NON RIAMMISSIBILI (`readmittable` non `true`, come
                  lo calcola l'anteprima del backend) si mostrano con la casella
                  SPENTA e lo stato visibile: la riga deve ESSERE LEGGIBILE, perché è
                  sull'elenco INTERO che si firma il patto del numero. È una
                  divergenza dichiarata dalla regola della finestra qui sopra
                  («non si mostra spento ciò che non si potrà mai»): là la
                  casella spenta prometterebbe un'azione, qui fa leggere un
                  elenco.
                  ⚠️ `status` può valere `null` — lo specchio dell'anno operativo
                  TACE. Si scrive «unknown status», non un vuoto e non un
                  «PENDING» inventato. */}
              {rientroModale.children.map((figlio) => {
                const statoFiglio = normalizza(figlio.status);
                const riammissibile = figlio.readmittable === true;

                return (
                  <label
                    key={figlio.id}
                    className={`qpsc-check${
                      !isAdmin || !riammissibile ? " qpsc-check--disabled" : ""
                    }`}
                  >
                    <input
                      className="qpsc-check__input"
                      type="checkbox"
                      checked={figliScelti.includes(figlio.id)}
                      onChange={() => toggleFiglio(figlio.id)}
                      disabled={!isAdmin || !riammissibile}
                    />
                    <span className="qpsc-check__box" aria-hidden="true" />
                    <span>
                      {figlio.name || figlio.id} — {statoFiglio || "unknown status"}
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="qpsc-modal__actions">
              {/* Scrittura riservata agli admin, come Approve e come il Save
                  della finestra delle squadre. */}
              {isAdmin && (
                <button className="qpsc-tool-btn" onClick={confermaRientro}>
                  Readmit
                </button>
              )}
              <button className="qpsc-ghost-btn" onClick={chiudiRientroModale}>
                {isAdmin ? "Cancel" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TEAM MANAGER MODAL */}
      <TeamManagerModal
        open={showTeamManager}
        onClose={() => setShowTeamManager(false)}
        teams={teams}
        onTeamsChange={setTeams}
        onUsersChange={ricaricaUtenti}
        isSuperAdmin={isSuperAdmin}
        stagione={stagioneAttiva}
      />

    </HomeShell>
  );
}
